<template>
  <div class="min-h-screen bg-white report-root">

    <!-- Side section progress nav -->
    <nav class="side-nav no-print" :class="{ 'side-nav--on-dark': navOnDark }" aria-label="Section navigation">
      <div class="side-nav-line"></div>
      <a
        v-for="s in progressSections"
        :key="s.id"
        :href="isSectionLocked(s.id) ? undefined : '#' + s.id"
        class="side-nav-item"
        :class="{ 'is-active': activeSection === s.id, 'is-locked': isSectionLocked(s.id) }"
        @click.prevent="scrollToSection(s.id)"
      >
        <span class="side-nav-label">
          <svg v-if="isSectionLocked(s.id)" class="side-nav-lock" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fill-rule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clip-rule="evenodd"/>
          </svg>
          {{ s.label }}
        </span>
        <span class="side-nav-dot"></span>
      </a>
    </nav>

    <!-- Sticky header -->
    <header class="bg-white border-b border-gray-100 sticky top-0 z-20 no-print header-bar shadow-sm">
      <div class="reading-progress" :style="{ width: scrollProgress + '%' }"></div>
      <div class="fl-container tw-flex justify-between items-center py-4">
        <button
          @click="$router.go(-1)"
          class="tw-flex items-center gap-2 text-base font-bold fl-blue-btn hover:opacity-70 transition-opacity bg-transparent border-none cursor-pointer p-0"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          Go Back
        </button>
        <nav class="tw-flex items-center gap-8 h-12">
          <!--          <a href="#executive-summary" class="fl-nav-link text-base font-medium hidden md:block">Executive Summary</a>
                    <a href="#section-1" class="fl-nav-link text-base font-medium hidden md:block">Signals</a>
                    <a href="#section-4" class="fl-nav-link text-base font-medium hidden md:block">Industry</a>
                    <a href="#section-6" class="fl-nav-link text-base font-medium hidden md:block">Conclusion</a>
                    <button
                      @click="exportPDF"
                      class="tw-flex items-center gap-2 text-sm font-semibold text-white bg-fl-green hover:opacity-90 px-4 py-2 rounded-lg border-none cursor-pointer transition-opacity"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                      </svg>
                      Export PDF
                    </button>-->
        </nav>
      </div>
    </header>

    <div id="report-content">

      <!-- Hero -->
      <section class="hero-section" ref="heroSection">
        <!-- Animated blobs -->
        <div class="hero-blob hero-blob-1"></div>
        <div class="hero-blob hero-blob-2"></div>
        <div class="hero-blob hero-blob-3"></div>
        <!-- Cursor glow (follows mouse in dark sections) -->
        <div class="hero-cursor-glow" :style="{ left: cursorX + 'px', top: cursorY + 'px' }" ref="heroGlow"></div>

        <div class="fl-container hero-inner">
          <div class="hero-eyebrow" :class="{ 'hero-revealed': heroRevealed }">Foodleap Deep Dive Report · May 2026</div>
          <div class="hero-accent-line" :class="{ 'hero-revealed': heroRevealed }"></div>
          <h1 class="report-h1" :class="{ 'hero-revealed': heroRevealed }">
            <span class="hero-word hero-word-1">Balanced</span>
            <br>
            <span class="hero-word hero-word-2 text-fl-green">Protein</span>
          </h1>
          <div class="hero-subtitle" :class="{ 'hero-revealed': heroRevealed }">Rebalancing health, affordability and sustainability in food</div>
          <p class="hero-body" :class="{ 'hero-revealed': heroRevealed }">A Foodleap Deep Dive exploring how balanced protein is emerging as a practical transition pathway across
            Dutch retail and the wider food system.</p>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 fl-kpi-grid" :class="{ 'hero-revealed': heroRevealed }">
            <div class="fl-kpi">
              <div class="kpi-number">{{ kpiDisplayValues[0] }}</div>
              <div class="kpi-label">Products analysed</div>
            </div>
            <div class="fl-kpi">
              <div class="kpi-number">{{ kpiDisplayValues[1] }}</div>
              <div class="kpi-label">Balanced products</div>
            </div>
            <div class="fl-kpi">
              <div class="kpi-number">{{ kpiDisplayValues[2] }}</div>
              <div class="kpi-label">Category penetration</div>
            </div>
            <div class="fl-kpi">
              <div class="kpi-number">{{ kpiDisplayValues[3] }}</div>
              <div class="kpi-label">Retailers analysed</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Executive Summary -->
      <section id="executive-summary" class="py-20 fl-section-alt scroll-mt-16 anim-fade-up">
        <div class="fl-container">
          <SectionHeader number="" title="EXECUTIVE SUMMARY"/>
          <div class="mt-8 space-y-5 leading-relaxed text-body fl-blue">
            <h3 class="text-base font-bold fl-blue">Balanced Protein – Rebalancing health, affordability and sustainability in food</h3>
            <p>For much of the past decade, the protein transition was framed primarily as a story of replacement. Plant-based burgers, dairy alternatives and next-generation
              proteins were positioned as substitutes for animal-based products, with the expectation that technological progress and consumer awareness would gradually shift
              mainstream consumption patterns.</p>
            <p>That first wave mattered. It accelerated awareness around sustainability, attracted investment, expanded retail assortments and pushed established food companies to
              rethink protein innovation. But it also exposed the limits of a transition model built primarily around direct substitution. Growth in several plant-based categories
              slowed, price premiums remained difficult to sustain, repeat purchases proved inconsistent, and concerns around ultra-processed foods increasingly influenced consumer
              perception.</p>
            <p>At the same time, the pressures facing the food industry continued to intensify. Health, affordability, sustainability, sourcing resilience and operational
              simplicity can no longer be managed separately. Retailers and food manufacturers are increasingly confronted with the challenge of improving nutritional profiles,
              maintaining affordability, reducing environmental impact and stabilizing supply chains, preferably simultaneously.</p>
            <p>Against this backdrop, a quieter and more pragmatic adaptation pattern is emerging across food retail.</p>
            <p>This report analyses the rapid rise of balanced protein products in Dutch retail: products in which animal-based ingredients are partially replaced or complemented
              by plant-based ingredients, fungi or other alternative protein components. While balanced products still represent a minority of total offerings, the market signals
              observed across Dutch retail suggest that hybridization is moving beyond isolated experimentation and gradually becoming integrated into mainstream portfolio
              strategies.</p>
            <p class="font-medium fl-blue">The findings reveal several important patterns.</p>
            <p>First, balanced protein adoption is not occurring randomly across categories. Products with greater formulation flexibility, such as sausages, burgers, meatballs
              and processed meat preparations, show significantly higher levels of hybridization than whole-cut or premium meat formats. This suggests that transition success is
              closely linked to category compatibility and operational feasibility, rather than consumer willingness alone.</p>
            <p>Second, balanced protein is being driven primarily through private label and retailer-led reformulation strategies. Dutch retailers are not operationalizing
              transition uniformly, but selectively: prioritizing formats, ingredient systems and implementation pathways where commercial and operational friction remains
              manageable.</p>
            <p>Third, much of this transition remains relatively invisible to consumers. A large share of balanced products communicates their hybrid composition only through
              back-of-pack ingredient information, rather than explicit front-of-pack sustainability messaging. This suggests that mainstream food-system adaptation may occur
              less through radical disruption and more through gradual reformulation inside familiar products and purchasing habits.</p>
            <p>The analysis also indicates that balanced products can contribute to several converging industry priorities simultaneously. Compared to non-balanced equivalents,
              many products demonstrate nutritional improvement, affordability, sourcing flexibility and operational feasibility.</p>

            <div class="border-l-fl pl-6 py-1 space-y-4">
              <p>Importantly, this report does not position balanced protein as a final destination or as a replacement for broader protein innovation. Rather, balanced protein
                should be understood as one practical transition pathway within a wider reformulation movement that is reshaping the food industry.</p>
              <p>The broader significance of balanced protein lies in what it reveals about how food systems may actually evolve.</p>
              <p>The next phase of food innovation is unlikely to be defined by one dominant protein source or technological breakthrough alone. Instead, evidence from Dutch
                retail suggests that the strongest solutions will be those that reduce friction across multiple dimensions.</p>
              <p><strong>Balanced protein emerged as a response to existing implementation challenges.</strong></p>
              <p>The key question is no longer simply whether alternative proteins can technically replace animal proteins. Increasingly, the challenge is how reformulation
                pathways can scale operationally across retailers, manufacturers, suppliers and existing consumption systems.</p>
              <p>This shift may have important implications for parts of the alternative protein industry itself. Much of the first wave was built around standalone replacement
                categories designed to compete directly with conventional meat systems. The emerging evidence from retail suggest that future scaling may increasingly depend on
                integration, reformulation partnerships and collaboration with existing food-system actors.</p>
              <p>Balanced protein therefore opens a broader strategic conversation for the food industry: not only about protein, but about how transitions become operationally
                achievable.</p>
              <p>The companies best positioned for the next phase of food-system adaptation may not necessarily be those pursuing the most radical disruption. Increasingly,
                competitive advantage may depend on the ability to detect emerging reformulation signals early, reduce implementation friction and coordinate adaptation
                pragmatically across interconnected food systems.</p>
              <p>At the same time, radical innovators remain essential in expanding what food-system transition can make possible. New protein sources, fermentation, cultivated
                ingredients, advanced processing, AI-enabled formulation and other emerging technologies continue to widen the range of future options. The challenge for the food
                industry is therefore not choosing between disruption and adaptation but understanding how breakthrough innovation and pragmatic reformulation can reinforce each
                other more effectively.</p>
            </div>
          </div>
          <div class="quote-block anim-fade-up">
            Balanced protein reveals that food-system transition is increasingly a question of coordination.
          </div>
        </div>
      </section>

      <!-- ── Upgrade CTA (trial users only) ── -->
      <section v-if="hasTrialOnly" class="pb-20 pt-20 fl-section-alt">
        <div class="fl-container">

          <!-- Upgrade card -->
          <div class="rounded-2xl overflow-hidden" style="background:#f4f9f1; border:1px solid #dcf0d8;">
            <div class="tw-flex flex-col lg:flex-row">

              <!-- Left -->
              <div class="flex-1 p-8 lg:p-10">
                <div class="tw-flex items-center gap-3 mb-5">
                  <div class="w-10 h-10 rounded-full tw-flex items-center justify-center shrink-0" style="background:#dcf0d8;">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color:#2d6a1f">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                  </div>
                  <span class="text-xs font-bold uppercase tracking-widest" style="color:#2d6a1f;">{{ $t('trp_fcta_badge') }}</span>
                </div>

                <h3 class="text-xl font-bold mb-3" style="color:#1a2e1a; font-family: Inter, system-ui, sans-serif;">
                  {{ $t('trp_upgrade_title') }}
                </h3>
                <p class="text-sm mb-6" style="color:#475569; font-family: Inter, system-ui, sans-serif; line-height:1.6;">
                  {{ $t('trp_upgrade_text') }}
                </p>

                <div class="tw-flex flex-col gap-3 mb-8">
                  <div v-for="section in lockedSections" :key="section" class="tw-flex items-center gap-3 text-sm font-medium"
                       style="color:#374151; font-family: Inter, system-ui, sans-serif;">
                    <svg class="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20" style="color:#9ca3af">
                      <path clip-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" fill-rule="evenodd"/>
                    </svg>
                    {{ section }}
                  </div>
                </div>

                <div class="tw-flex flex-wrap items-center gap-3">
                  <button
                    @click="$router.push('/transition-reports/trial')"
                    class="px-6 py-3 rounded-xl text-sm font-bold tw-flex items-center gap-2 transition-opacity hover:opacity-90"
                    style="background:#2d6a1f; color:#fff; border:none; cursor:pointer;"
                  >
                    {{ $t('trp_subscribe_now') }}
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </button>
                  <button
                    @click="$router.push('/about#contact')"
                    class="px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
                    style="border:1px solid #e2e8f0; color:#475569; background:transparent; cursor:pointer;"
                  >
                    {{ $t('trp_contact_us') }}
                  </button>
                </div>
              </div>

              <!-- Right: price -->
              <div class="lg:w-60 shrink-0 p-8 lg:p-10 tw-flex flex-col justify-center items-center text-center"
                   style="background:rgba(255,255,255,0.5); border-left:1px solid #dcf0d8;">
                <svg class="w-10 h-10 mb-5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color:#2d6a1f">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                <p class="text-xs uppercase tracking-widest font-semibold mb-2" style="color:#6b7280; font-family:Inter,sans-serif;">{{ $t('trp_founding_member') }}</p>
                <p class="text-4xl font-extrabold mb-1" style="color:#1a2e1a; font-family:Inter,sans-serif;">€9,000</p>
                <p class="text-sm mb-5" style="color:#6b7280; font-family:Inter,sans-serif;">{{ $t('trp_per_year') }}</p>
                <p class="text-xs leading-relaxed" style="color:#9ca3af; font-family:Inter,sans-serif;">{{ $t('trp_fcta_early_pricing') }}</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      <!-- ── Locked sections (full subscription only) ── -->
      <template v-if="hasFullAccess">

        <!-- Section 1 -->
        <section id="section-1" class="py-20 fl-section scroll-mt-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <SectionHeader number="1" title="RETAIL SIGNALS REVEAL A NEW REFORMULATION LOGIC"/>
            <div class="space-y-5 leading-relaxed text-body fl-blue">
              <p>Much of the public debate around protein transition has focused on replacement: replacing meat with plant-based alternatives, replacing dairy with plant-based
                drinks, or developing entirely new protein categories through fermentation and cultivated technologies.</p>
              <p>But inside Dutch retail, another pattern is quietly emerging. Rather than scaling primarily through entirely new categories, adaptation increasingly appears to
                happen through reformulation inside familiar products and existing retail structures. Burgers become partially plant-based. Meatballs integrate fibres or
                vegetables.
                Sausages evolve into hybrid formulations. Protein systems diversify gradually, often without fundamentally changing the products consumers already know and buy.</p>
              <p>To better understand these emerging dynamics, this report analyzed 7,714 food products across Albert Heijn, Jumbo and SPAR, focusing on the rise of balanced
                formulations combining animal and plant-based proteins within the same product architecture.</p>
              <p>Rather than evaluating balanced protein purely as a standalone category, the objective was to understand what these retail signals reveal about how food systems
                are
                beginning to adapt to converging pressures around affordability, nutrition, sourcing resilience, sustainability, and operational continuity.</p>
              <p>The findings suggest that balanced protein is important not simply because it represents another protein category, but because it reveals a broader shift in how
                food
                innovation is beginning to scale: incrementally, selectively, and through lower-friction reformulation pathways integrated into existing portfolios and familiar
                consumption habits.</p>
            </div>

          </div>
        </section>

        <!-- Scroll hint: fixed, shown only while in signals section -->
        <div class="sh-scroll-hint" ref="signalScrollHint">
          <span class="sh-scroll-hint-text">Scroll to navigate signals</span>
          <svg class="sh-scroll-hint-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 5v14M5 12l7 7 7-7"/>
          </svg>
        </div>

        <!-- ── Horizontal signal scroll ───────────────────────────── -->
        <div class="signals-hscroll" ref="signalsHscroll" :style="{ height: (signals.length * 100 + 20) + 'vh' }">
          <div class="signals-hscroll-pin">
            <div class="sh-pin-wrapper">

              <!-- Top bar -->
              <div class="signals-hscroll-bar">
                <div>
                  <div class="sh-eyebrow">Section 1 · Retail Signals</div>
                  <div class="sh-bar-title">6 patterns from Dutch retail</div>
                </div>
                <div class="sh-dots">
                  <div v-for="(_, i) in signals" :key="i" class="sh-dot" :data-sh-dot="i"></div>
                </div>
                <div class="sh-counter">
                  <span class="sh-counter-current">1</span>
                  <span class="sh-counter-denom"> / {{ signals.length }}</span>
                </div>
              </div>

              <!-- Horizontal track -->
              <div class="sh-viewport">
                <div class="sh-track">
                  <div v-for="(signal, i) in signals" :key="i" class="sh-card">
                    <div class="sh-card-watermark">{{ String(i + 1).padStart(2, '0') }}</div>
                    <div class="sh-card-inner">
                      <div class="sh-card-top">
                        <span class="sh-card-label">Signal {{ String(i + 1).padStart(2, '0') }}</span>
                        <h3 class="sh-card-title">{{ signalTitles[i] }}</h3>
                      </div>
                      <div class="sh-card-body">
                        <div class="sh-entry">
                          <div class="sh-entry-tag">Observation</div>
                          <p class="sh-entry-text">{{ signal.observe }}</p>
                        </div>
                        <div class="sh-entry">
                          <div class="sh-entry-tag">Insight</div>
                          <p class="sh-entry-text">{{ signal.reveals }}</p>
                        </div>
                        <div class="sh-entry sh-entry--impl">
                          <div class="sh-entry-tag">Implication</div>
                          <p class="sh-entry-text">{{ signal.implication }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>

        <!-- Section 1 close-out: infographic + summary -->
        <div class="fl-section py-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <div class="fl-infographic">
              <img src="/images/reports/balanced-protein/img-009.png" alt="Retail signals from the Dutch market" class="w-full"/>
            </div>
            <div class="space-y-4 leading-relaxed text-body fl-blue">
              <p>These signals suggest that the protein transition is entering a new implementation phase. The emerging retail patterns point toward gradual reformulation inside
                mainstream portfolios, where adaptation scales selectively through products that remain operationally feasible, commercially viable and behaviourally familiar.</p>
              <p>Large-scale food-system adaptation increasingly depends on the ability to integrate change pragmatically across existing retail structures, procurement systems and
                consumption habits. Balanced protein reveals how this integration logic is beginning to materialize in practice.</p>
            </div>
          </div>
        </div>

        <!-- Key Findings — sticky left stat + scroll-driven right slides -->
        <div class="sticky-story" ref="stickyStory" :style="{ height: scrollContainerH ? (storySlides.length * scrollContainerH) + 'px' : (storySlides.length * 100) + 'vh' }">
          <div class="sticky-story-pin">

            <!-- Left: stat updated via direct DOM on scroll -->
            <div class="sticky-story-left">
              <div class="sticky-stat-display">
                <div class="sticky-stat-eyebrow">Key finding 1</div>
                <div class="sticky-stat-number">{{ storySlides[0].stat }}</div>
                <div class="sticky-stat-unit">{{ storySlides[0].unit }}</div>
              </div>
              <div class="sticky-story-dots">
                <div
                  v-for="(_, i) in storySlides"
                  :key="i"
                  class="sticky-dot"
                ></div>
              </div>
            </div>

            <!-- Right: slides translate upward via scroll -->
            <div class="sticky-story-right-viewport">
              <div class="sticky-story-track" ref="storyTrack">
                <div v-for="(slide, i) in storySlides" :key="i" class="story-slide">
                  <h3 class="story-slide-title">{{ slide.title }}</h3>
                  <p class="story-slide-body">{{ slide.body }}</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Section 2 -->
        <section id="section-2" class="py-20 fl-section scroll-mt-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <SectionHeader number="2" title="WHY REPLACEMENT-FIRST STRATEGIES HIT LIMITS"/>
            <div class="space-y-8 leading-relaxed text-body fl-blue">
              <div>
                <h3 class="subsection-heading mb-3">2.1 The first wave created momentum</h3>
                <div class="space-y-4">
                  <p>The first wave of protein transition significantly increased attention around protein diversification within the global food industry. Plant-based meat and
                    dairy
                    alternatives moved from niche positioning into mainstream retail. Retailers expanded assortments, investors poured capital into alternative protein startups,
                    and
                    food manufacturers accelerated experimentation around plant-based reformulation, fermentation technologies and cultivated meat.</p>
                  <p>This first phase played an important role in increasing consumer awareness around sustainability, normalizing discussions around alternative proteins and
                    pushing
                    established food companies to explore new protein strategies. It also accelerated innovation across ingredient systems, protein functionality and food
                    formulation
                    technologies, expanding the range of technical capabilities available to manufacturers and retailers.</p>
                  <p>Moreover, the first wave contributed to the emergence of a broader ecosystem around protein diversification, connecting startups, ingredient suppliers,
                    retailers,
                    manufacturers, investors and policymakers around a shared transition narrative.</p>
                </div>
              </div>
              <div>
                <h3 class="subsection-heading mb-3">2.2 The limits of direct substitution became visible</h3>
                <div class="space-y-4">
                  <p>As the market matured, however, several structural tensions became increasingly difficult to ignore. Much of the first wave was built around direct replacement
                    logic: if plant-based products could sufficiently replicate meat and dairy on taste, texture, nutrition and price, consumers would eventually switch. In
                    practice,
                    large-scale adoption proved more complex.</p>
                  <p>Consumers continued to express interest in healthier and more sustainable diets yet repeat purchase across several plant-based categories remained uneven.
                    Price
                    premiums were difficult to sustain under growing inflationary pressure, while concerns around taste, texture and habits continued to limit broader mainstream
                    adoption.</p>
                  <p>At the same time, public discussions around ultra-processed foods (UPF), ingredient complexity and artificiality increasingly affected consumer perception of
                    highly engineered meat alternatives.</p>
                  <p>The market also became increasingly fragmented in how protein transition was framed. Over time, alternative proteins were associated with multiple overlapping
                    promises around sustainability, health, animal welfare, local sourcing and protein optimization. While these narratives helped broaden market experimentation,
                    they
                    also created growing ambiguity around what alternative proteins were ultimately expected to deliver and for whom.</p>
                  <p>At the same time, everyday consumer purchasing behaviour often remained far more pragmatic than the transition narratives surrounding the category. Consumers
                    continued to prioritize familiar decision drivers such as taste, price, convenience and routine.</p>
                  <p>Meanwhile, plant-based categories increasingly faced pressure. While the category continued to generate attention and innovation activity, retail growth slowed
                    across several markets, particularly under inflationary pressure and growing consumer scrutiny around taste and ultra-processed foods. This also created growing
                    uncertainty around how alternative proteins could scale consistently within mainstream retail systems.</p>
                </div>
              </div>
              <div>
                <h3 class="subsection-heading mb-3">2.3 Scaling proved more complex than expected</h3>
                <div class="space-y-4">
                  <p>As a result, the transition increasingly moved beyond the question of whether alternatives could be developed technologically. The central challenge became
                    whether these solutions could integrate naturally into everyday purchasing behaviour, existing category structures and operational retail systems.</p>
                  <p>As the first wave of protein transition matured, it became increasingly clear that different parts of the ecosystem were encountering different scaling
                    bottlenecks.</p>
                  <p>For many consumer-facing alternative protein brands, the primary challenge was no longer technological feasibility or manufacturing infrastructure. Significant
                    investment capital was available, retailers had expanded assortments, and large food companies were prepared to scale production capacity further if mainstream
                    demand continued growing. But repeat purchase remained inconsistent across several categories. While consumers expressed growing interest in sustainability and
                    healthier diets, everyday purchasing behaviour did not follow.</p>
                  <p>At the same time, upstream ingredient innovators and food technology startups faced a different set of scaling challenges altogether.</p>
                  <p>Without sufficient scale, ingredient costs remained high. Without stable demand growth, manufacturers and retailers were often unwilling to absorb the
                    operational
                    and commercial risk required to accelerate adoption further. As a result, many promising technologies became trapped in a difficult scaling loop: insufficient
                    scale
                    limited competitiveness, while limited market adoption reduced the investment certainty required to scale.</p>
                </div>
              </div>
            </div>

            <!-- Key Takeaway Infographic Section 2 -->
            <div class="fl-infographic anim-fade-up">
              <img src="/images/reports/balanced-protein/img-012.png" alt="Why replacement-first strategies hit limits" class="w-full"/>
            </div>
          </div>
        </section>

        <!-- Section 3 -->
        <section id="section-3" class="py-20 fl-section scroll-mt-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <SectionHeader number="3" title="BALANCED PROTEIN AS A PRACTICAL TRANSITION PATHWAY"/>

            <!-- 3.1 -->
            <div class="space-y-6 leading-relaxed text-body fl-blue">
              <h3 class="subsection-heading">3.1 Shift from replacement toward integration</h3>
              <p>Evidence from Dutch retail suggests that large food systems rarely transform through abrupt substitution alone. Retailers need categories that perform
                commercially.
                Manufacturers need formulations that scale operationally. Consumers gravitate toward products that fit existing habits, routines and expectations.</p>
              <p>As a result, the market increasingly appears to evolve through adaptation rather than replacement. This helps explain why balanced protein is emerging most
                strongly
                inside familiar formats rather than in entirely new consumption models. Hybrid burgers, meatballs and sausages do not require consumers to fundamentally reinvent
                eating behaviour. Instead, they gradually rebalance ingredient systems within products that already fit everyday purchasing routines.</p>
              <p>This pattern reflects a broader characteristic of large-scale system transitions. Food systems are deeply interconnected through procurement structures,
                manufacturing infrastructure, retailer category management, pricing models, sourcing systems, culinary habits and operational constraints.</p>
              <p>The more easily innovation can integrate into these existing structures, the easier it becomes to scale.</p>
              <p>Balanced protein appears particularly relevant in this context because it reduces friction across multiple parts of the food system simultaneously.</p>
            </div>

            <!-- Key Takeaway Infographic Section 3.1 -->
            <div class="fl-infographic anim-fade-up">
              <img src="/images/reports/balanced-protein/img-015.png" alt="Balanced protein reduces transition friction" class="w-full"/>
            </div>

            <!-- Friction reduction — numbered pillars -->
            <div class="fric-pillars">

              <div class="fric-pillar anim-fade-up" style="transition-delay: 0ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">01</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">Consumer behaviour</div>
                  <h4 class="fric-pillar-title">Consumer familiarity</h4>
                  <p class="fric-pillar-body">Many balanced products remain embedded inside familiar formats such as burgers, sausages, meatballs and convenience products. This
                    allows adaptation to happen without requiring consumers to fundamentally change eating habits or product expectations.</p>
                  <p class="fric-pillar-body">Familiarity is an important adoption mechanism. This also explains why reformulation frequently remains subtle or partially invisible
                    inside mainstream retail portfolios.</p>
                </div>
              </div>

              <div class="fric-pillar anim-fade-up" style="transition-delay: 80ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">02</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">Manufacturing</div>
                  <h4 class="fric-pillar-title">Operational continuity</h4>
                  <p class="fric-pillar-body">Balanced formulations fit more naturally within existing manufacturing systems than many entirely new product architectures. Processed
                    and structured meat categories already depend heavily on ingredient systems to shape texture, flavour, juiciness and consistency. Integrating plant proteins,
                    fibres or functional ingredients into these formats requires adaptation rather than complete operational redesign.</p>
                  <p class="fric-pillar-body">This lowers implementation complexity for manufacturers while preserving much of the existing production infrastructure. The strong
                    concentration of balanced products in processed formats such as sausages, burgers and meatballs reflects this operational compatibility.</p>
                </div>
              </div>

              <div class="fric-pillar anim-fade-up" style="transition-delay: 160ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">03</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">Commercial</div>
                  <h4 class="fric-pillar-title">Pricing &amp; procurement</h4>
                  <p class="fric-pillar-body">Affordability increasingly shapes transition feasibility across the food industry. Balanced formulations offer retailers and
                    manufacturers greater flexibility in managing ingredient costs, sourcing volatility and margin pressure compared to systems relying entirely on high-cost
                    alternative proteins or volatile animal inputs alone.</p>
                  <p class="fric-pillar-body">The growing dominance of private-label balanced products further reinforces this dynamic. Retailers increasingly integrate balanced
                    protein through procurement and portfolio strategies designed to optimize pricing, scalability and category performance simultaneously. This shifts balanced
                    protein from a niche innovation discussion toward a broader portfolio management and sourcing strategy.</p>
                </div>
              </div>

              <div class="fric-pillar anim-fade-up" style="transition-delay: 240ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">04</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">Market structure</div>
                  <h4 class="fric-pillar-title">Category compatibility</h4>
                  <p class="fric-pillar-body">The retail analysis demonstrates that adoption remains highly uneven across categories. Products with high formulation flexibility,
                    such as frikandels, burgers and meatballs, show significantly higher balanced penetration than whole-cut and premium formats.</p>
                  <p class="fric-pillar-body">This suggests that successful transition pathways depend heavily on compatibility with existing sensory expectations, operational
                    structures and consumption habits. The challenge is therefore identifying where adaptation can integrate most naturally into existing food-system
                    architectures.</p>
                </div>
              </div>

              <div class="fric-pillar fric-pillar--last anim-fade-up" style="transition-delay: 320ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">05</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">System dynamics</div>
                  <h4 class="fric-pillar-title">Incremental adaptation</h4>
                  <p class="fric-pillar-body">One of the most important characteristics of balanced protein is that it allows adaptation to happen progressively. Rather than
                    requiring abrupt replacement of existing categories, balanced formulations enable manufacturers and retailers to gradually rebalance ingredient systems over
                    time.</p>
                  <p class="fric-pillar-body">This incremental logic may prove particularly important in large-scale food systems where procurement structures, manufacturing
                    assets, consumer routines and retail categories cannot transform overnight. Balanced protein functions increasingly as a transition mechanism capable of
                    reducing friction across interconnected parts of the food system simultaneously. Its relevance lies in its ability to integrate pragmatically into existing
                    retail systems, manufacturing realities and everyday consumption habits.</p>
                </div>
              </div>

            </div>

            <!-- 3.2 -->
            <h3 class="subsection-heading">3.2 The role of private label and procurement systems</h3>
            <div class="space-y-4 leading-relaxed text-body fl-blue">
              <p>One of the clearest signals emerging from the Dutch retail analysis is the central role retailers increasingly play in operationalizing food-system adaptation.
                Balanced protein is not scaling primarily through standalone challenger brands or isolated innovation niches. Instead, much of the implementation is occurring
                through
                private-label portfolios, procurement systems and retailer-controlled reformulation strategies integrated directly into mainstream assortments. As a result,
                large-scale food-system adaptation increasingly depends on how retailers, procurement teams and manufacturers integrate reformulation into existing portfolio
                systems.</p>
            </div>

            <!-- Retailer role numbered callouts -->
            <div class="retailer-role-list anim-fade-up">
              <div class="retailer-role-item">
                <div class="retailer-role-num">01</div>
                <div class="retailer-role-content">
                  <h4 class="retailer-role-title">Retail as transition infrastructure</h4>
                  <p class="retailer-role-body">Traditionally, retailers primarily acted as gatekeepers between manufacturers and consumers by selecting assortments, allocating
                    shelf space, and managing pricing dynamics. The retail signals emerging around balanced protein suggest retailers are increasingly taking on a more active
                    orchestration role.</p>
                  <p class="retailer-role-body mt-2">Private-label systems give retailers direct influence over formulation choices, sourcing strategies, ingredient systems,
                    nutritional positioning, communication approaches and pricing architecture. This creates the ability to operationalize reformulation at portfolio level rather
                    than product-by-product. The strong dominance of private label within balanced protein categories reflects this shift clearly. Hybridization increasingly
                    behaves like a portfolio adaptation strategy embedded inside retailer-controlled systems.</p>
                </div>
              </div>
              <div class="retailer-role-item">
                <div class="retailer-role-num">02</div>
                <div class="retailer-role-content">
                  <h4 class="retailer-role-title">Procurement-led scaling</h4>
                  <p class="retailer-role-body">The growing role of procurement systems is particularly important in understanding how food-system adaptation scales in practice.
                    Retailers operate at the intersection of pricing pressure, sourcing volatility, consumer expectations, category performance, nutritional targets and
                    sustainability ambitions. Balanced protein aligns with many of these operational realities because it creates greater flexibility across ingredient sourcing and
                    formulation architectures while maintaining compatibility with existing product structures.</p>
                  <p class="retailer-role-body mt-2">This allows retailers to gradually integrate adaptation into mainstream volumes without requiring abrupt category disruption or
                    complete portfolio redesign. Balanced protein therefore increasingly scales through procurement choices, ingredient selection, reformulation decisions and
                    supplier coordination.</p>
                </div>
              </div>
              <div class="retailer-role-item">
                <div class="retailer-role-num">03</div>
                <div class="retailer-role-content">
                  <h4 class="retailer-role-title">Mainstream volume integration</h4>
                  <p class="retailer-role-body">Perhaps the most important implication is that balanced protein integrates directly into high-volume mainstream categories rather
                    than remaining confined to niche alternative segments. Processed meat categories such as burgers, sausages and meatballs already represent large-scale everyday
                    consumption formats. Integrating reformulation into these categories creates significantly larger leverage potential than relying exclusively on separate
                    alternative-protein shelves.</p>
                  <p class="retailer-role-body mt-2">This changes the logic of scaling. Balanced protein enables adaptation to happen inside existing consumption structures rather
                    than outside them. The retail analysis suggests that food-system transition increasingly scales through procurement systems, portfolio strategies and
                    reformulation architectures rather than through niche innovation categories.</p>
                </div>
              </div>
            </div>

            <!-- Pull quote: retailers as infrastructure -->
            <div class="pull-quote anim-fade-up">
              <div class="pull-quote-mark">"</div>
              <p class="pull-quote-text">Retailers increasingly function as transition infrastructure — integrating adaptation into mainstream food systems through sourcing,
                reformulation and portfolio orchestration at scale.</p>
            </div>

            <!-- Key Takeaway Infographic Section 3.2 -->
            <div class="fl-infographic anim-fade-up">
              <img src="/images/reports/balanced-protein/img-018.png" alt="The role of private label and procurement systems" class="w-full"/>
            </div>

            <!-- 3.3 -->
            <h3 class="subsection-heading">3.3 Multifunctional reformulation</h3>
            <div class="space-y-4 leading-relaxed text-body fl-blue">
              <p>One of the clearest shifts emerging from the retail analysis is that balanced protein is increasingly becoming part of a broader reformulation movement rather than
                remaining a standalone protein-transition category.</p>
              <p>The emerging retail patterns suggest the market is gradually moving toward a more multifunctional logic. Protein is increasingly becoming one component within
                broader formulation strategies balancing nutrition, satiety, ingredient functionality, affordability, sensory performance and resilience at the same time. This is
                particularly visible in balanced formulations where plant proteins, fibres, vegetables and functional ingredients are combined inside familiar products rather than
                positioned as entirely separate nutritional systems.</p>
            </div>

            <!-- Multifunctional pillars -->
            <div class="fric-pillars">

              <div class="fric-pillar anim-fade-up" style="transition-delay: 0ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">01</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">Health &amp; nutrition</div>
                  <h4 class="fric-pillar-title">Nutritional optimization</h4>
                  <p class="fric-pillar-body">The growing relevance of balanced protein also aligns with wider industry pressure around nutritional reformulation. Retailers and
                    manufacturers are increasingly expected to improve nutritional quality, fibre intake, ingredient diversity and overall health performance, while maintaining
                    affordability and mainstream accessibility.</p>
                  <p class="fric-pillar-body">Balanced formulations create additional flexibility in achieving these objectives because ingredient systems can be optimized across
                    multiple nutritional dimensions. This may partially explain why balanced products increasingly combine protein diversification, fibre enrichment, vegetable
                    integration and reformulation strategies aimed at improving overall nutritional profiles. The market therefore appears to be moving away from isolated 'protein
                    thinking' toward broader nutritional optimization.</p>
                </div>
              </div>

              <div class="fric-pillar anim-fade-up" style="transition-delay: 80ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">02</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">Consumer wellness</div>
                  <h4 class="fric-pillar-title">Satiety &amp; health</h4>
                  <p class="fric-pillar-body">The growing focus on satiety, metabolic health and functional nutrition further reinforces this shift. Consumers increasingly evaluate
                    food on energy balance, perceived naturalness, ingredient quality and overall health impact. At the same time, growing attention around satiety, weight
                    management and preventive health is likely to further increase industry interest in foods capable of delivering stronger nutritional density and longer-lasting
                    fullness within mainstream consumption patterns.</p>
                  <p class="fric-pillar-body">This creates additional momentum for reformulation strategies capable of combining protein, fibre, functional ingredients and sensory
                    characteristics inside scalable everyday products. Balanced protein therefore increasingly aligns with broader health and wellness transitions extending beyond
                    protein diversification alone.</p>
                </div>
              </div>

              <div class="fric-pillar anim-fade-up" style="transition-delay: 160ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">03</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">Ingredient strategy</div>
                  <h4 class="fric-pillar-title">Functional ingredient systems</h4>
                  <p class="fric-pillar-body">The retail analysis also reveals growing diversification across ingredient systems. Balanced products increasingly combine plant
                    proteins, fibres, vegetables, fungi, algae, fermentation-derived ingredients and functional binders within increasingly flexible formulation architectures.</p>
                  <p class="fric-pillar-body">This reflects a broader evolution in ingredient strategy. Increasingly, value depends on the ability to deliver multiple
                    functionalities such as texture, water binding, nutritional performance, sourcing resilience, clean-label positioning, affordability and operational
                    compatibility at the same time. As a result, ingredients increasingly behave as multifunctional system components rather than isolated nutritional inputs.</p>
                </div>
              </div>

              <div class="fric-pillar fric-pillar--last anim-fade-up" style="transition-delay: 240ms">
                <div class="fric-pillar-left">
                  <div class="fric-pillar-num">04</div>
                </div>
                <div class="fric-pillar-right">
                  <div class="fric-pillar-eyebrow">Formulation design</div>
                  <h4 class="fric-pillar-title">Flexible formulation architectures</h4>
                  <p class="fric-pillar-body">Perhaps most importantly, balanced protein demonstrates how food systems may gradually evolve toward more flexible formulation
                    architectures overall. Rather than relying on one dominant protein source or one ideal replacement technology, manufacturers increasingly appear to build
                    adaptable ingredient systems capable of evolving over time in response to pricing volatility, sourcing dynamics, nutritional requirements, consumer
                    expectations, regulatory shifts and sustainability pressures.</p>
                  <p class="fric-pillar-body">This flexibility may ultimately become one of the most important competitive advantages in future food systems. The strategic value of
                    balanced protein therefore lies in demonstrating how reformulation can become more adaptive, modular and multifunctional across the broader food industry.</p>
                </div>
              </div>

            </div>

            <!-- Closing paragraph + pull quote -->
            <p class="text-body fl-blue leading-relaxed">The market increasingly appears to be shifting from single-purpose innovation toward multifunctional reformulation. The
              strategic relevance of balanced protein lies less in hybridization itself and more in what it reveals about the future of food innovation: adaptive systems capable of
              balancing multiple operational realities simultaneously.</p>

            <div class="pull-quote anim-fade-up">
              <div class="pull-quote-mark">"</div>
              <p class="pull-quote-text">The strategic value of balanced protein lies in demonstrating how reformulation can become more adaptive, modular and multifunctional —
                shifting from single-purpose innovation toward food systems capable of balancing multiple operational realities simultaneously.</p>
            </div>

            <!-- Key Takeaway Infographic Section 3.3 -->
            <div class="fl-infographic anim-fade-up">
              <img src="/images/reports/balanced-protein/img-021.png" alt="Multifunctional reformulation" class="w-full"/>
            </div>
          </div>
        </section>

        <!-- Section 4 intro -->
        <section id="section-4" class="py-20 fl-section scroll-mt-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <div class="display-stat-moment anim-fade-up">
              <div class="display-stat-number">17.5<span class="display-stat-suffix">%</span></div>
              <div class="display-stat-label">of all products analysed carry a balanced protein formulation</div>
              <div class="display-stat-sub">Across Albert Heijn, Jumbo and SPAR · May 2026</div>
            </div>
            <SectionHeader number="4" title="IMPLICATIONS FOR THE FOOD INDUSTRY"/>
            <p class="text-body text-gray-600 leading-relaxed">Balanced protein reveals that food-system transition is increasingly becoming a coordination challenge. Competitive
              advantage depends on aligning sourcing, formulation, procurement, scalability, nutrition and consumer adoption across interconnected actors. Scroll through the value
              chain to explore what this means for each stakeholder.</p>
          </div>
        </section>

        <!-- Section 4: Value chain scroll -->
        <div class="vc-section" :style="{ height: scrollContainerH ? (vcActors.length * scrollContainerH) + 'px' : (vcActors.length * 100) + 'vh' }">
          <div class="vc-pin">

            <!-- Chain navigation -->
            <div class="vc-chain-bar">
              <div class="vc-chain-inner">
                <template v-for="(actor, i) in vcActors" :key="i">
                  <div class="vc-node" :data-vc-node="i" :data-accent="actor.accent">
                    <div class="vc-node-dot"></div>
                    <div class="vc-node-label">{{ actor.short }}</div>
                  </div>
                  <div v-if="i < vcActors.length - 1" class="vc-connector"></div>
                </template>
              </div>
            </div>

            <!-- Actor identity (left) + content slides (right) -->
            <div class="vc-body">
              <div class="vc-actor-panel">
                <div class="vc-actor-num">01</div>
                <div class="vc-actor-name">Farmers &amp; Primary Production</div>
                <div class="vc-actor-of">Actor 1 of {{ vcActors.length }}</div>
              </div>
              <div class="vc-right-vp">
                <div class="vc-track" ref="vcTrack">
                  <div v-for="(actor, i) in vcActors" :key="i" class="vc-slide">
                    <div class="vc-rows-grid">
                      <div v-for="(row, j) in implication_rows[i]" :key="j" class="vc-row">
                        <div class="vc-row-tag" :style="{ color: row.color === '#21244A' ? '#a5b4fc' : row.color }">{{ row.label }}</div>
                        <p class="vc-row-text">{{ row.text }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Progress dots -->
            <div class="vc-dots-row">
              <div v-for="(_, i) in vcActors" :key="i" class="vc-dot-item" :data-vc-dot="i"></div>
            </div>

          </div>
        </div>

        <!-- Section 4 outro -->
        <div class="fl-section py-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <div class="fl-takeaway leading-relaxed text-body fl-blue">
              <p>Food-system transition increasingly depends on how effectively different actors across the value chain can align around shared pressures and constraints. The
                companies best positioned may not be those making the boldest claims, but those most capable of reducing implementation friction, coordinating across stakeholders
                and translating innovation into scalable, economically viable and operationally realistic solutions.</p>
            </div>
            <div class="fl-infographic anim-fade-up">
              <img src="/images/reports/balanced-protein/img-025.png" alt="A collective answer to pressures and tensions" class="w-full"/>
            </div>
          </div>
        </div>

        <!-- Section 5 -->
        <section id="section-5" class="py-20 fl-section scroll-mt-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <SectionHeader number="5" title="WHAT MATTERS NEXT"/>
            <div class="space-y-6 leading-relaxed text-body fl-blue">
              <div>
                <p class="font-bold fl-blue mb-2">Reformulation becomes strategic infrastructure</p>
                <p>Balanced protein demonstrates how adaptation can happen progressively inside existing products, portfolios and supply systems. Reformulation is increasingly
                  evolving from a product-level activity toward a core operational capability across the food industry. The companies best positioned for the next phase of
                  transition
                  may increasingly be those capable of continuously adapting formulations in function of scalability, affordability or consumer behaviour.</p>
              </div>
              <div>
                <p class="font-bold fl-blue mb-2">Procurement shapes adoption</p>
                <p>The transition increasingly scales through procurement, private label, ingredient specifications and portfolio management. Retailers and manufacturers influence
                  transition speed through sourcing choices and reformulation decisions integrated into mainstream categories.</p>
              </div>
              <div>
                <p class="font-bold fl-blue mb-2">Multifunctionality becomes standard</p>
                <p>Products are increasingly evaluated on their ability to balance taste, nutritional performance, affordability, operational simplicity within scalable
                  systems.</p>
              </div>
              <div>
                <p class="font-bold fl-blue mb-2">Breakthrough innovation continues expanding possibilities</p>
                <p>Balanced protein demonstrates how adaptation can scale inside existing food systems. At the same time, breakthrough technologies continue to expand what may
                  become
                  technically and operationally possible in the future. Technologies such as precision fermentation, cultivated ingredients, AI-enabled formulation and advanced
                  processing systems will gradually introduce new ingredient systems, functionalities and reformulation pathways over time. The challenge increasingly lies in
                  understanding where these technologies create meaningful operational value inside scalable food systems.</p>
              </div>
              <div>
                <p class="font-bold fl-blue mb-2">Coordination becomes strategic capability</p>
                <p>As food systems become more interconnected, adaptation increasingly depends on coordination across the value chain. The companies best positioned for the next
                  phase
                  of transition may not necessarily be those making the boldest claims, but those most capable of reducing implementation friction, aligning stakeholders and
                  translating innovation into scalable operational realities.</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Section 6 -->
        <section id="section-6" class="py-20 fl-section scroll-mt-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <SectionHeader number="6" title="FOOD-SYSTEM TRANSITION IS A QUESTION OF COORDINATION"/>
            <div class="space-y-5 leading-relaxed text-body fl-blue">
              <p>The protein transition is entering a more operational phase. Large-scale adaptation scales when innovation aligns with consumer behaviour, operational realities,
                procurement systems, manufacturing constraints and economic pressure across the broader food ecosystem.</p>
              <p>Balanced protein matters because it demonstrates how transition can integrate into existing food systems progressively rather than through abrupt replacement
                alone.</p>
              <p>This shift has implications far beyond protein itself. The next phase of food innovation may increasingly depend on the ability to reduce implementation friction,
                coordinate across value-chain actors, continuously adapt formulations and translate emerging innovation into scalable operational realities.</p>
              <p>In that sense, the future competitive advantage of the food industry may not necessarily lie in predicting the next trend or developing the most radical standalone
                product. It may increasingly depend on understanding how food systems adapt in practice: incrementally, multifunctionally, and across interconnected stakeholders
                simultaneously.</p>
              <p>Balanced protein reveals that food-system transition is increasingly a question of coordination.</p>
            </div>

            <div class="quote-block anim-fade-up">
              Balanced protein reveals that food-system transition is increasingly a question of coordination.
            </div>

            <!-- Three waves infographic -->
            <div class="fl-infographic anim-fade-up">
              <img src="/images/reports/balanced-protein/img-028.png" alt="The evolution of the protein shift" class="w-full"/>
            </div>
          </div>
        </section>

        <!-- Appendix -->
        <section id="appendix" class="py-20 fl-section scroll-mt-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <SectionHeader number="" title="APPENDIX 1 – DUTCH RETAIL ANALYSIS"/>
            <div class="space-y-5 leading-relaxed text-body fl-blue">
              <p>To validate the emerging reformulation dynamics observed throughout this report, Foodleap analyzed 7,714 food products across Albert Heijn, Jumbo and SPAR,
                identifying 362 intentionally blended products combining animal and plant-based ingredients within the same formulation architecture.</p>
              <p>The objective of this appendix is not to present balanced protein as a standalone category, but to provide additional evidence behind the six retail signals
                discussed in Chapter 1.</p>
              <p>The analysis illustrates how reformulation is increasingly integrated into mainstream food systems through familiar categories, retailer-led portfolio strategies
                and multifunctional ingredient systems.</p>

              <!-- Signal cards -->
              <div class="ax-signals mt-6">

                <div class="ax-sig">
                  <div class="ax-sig-watermark">01</div>
                  <div class="ax-sig-head">
                    <span class="ax-sig-num">01</span>
                    <div>
                      <div class="ax-sig-eyebrow">Signal</div>
                      <div class="ax-sig-title">Balanced protein enters mainstream retail portfolios</div>
                    </div>
                  </div>
                  <p class="ax-sig-text">Balanced formulations increasingly appear across familiar mainstream categories. The highest penetration is observed in processed and
                    convenience-oriented formats such as kroket, frikandel, nuggets and meatballs.</p>
                  <img src="/images/reports/balanced-protein/img-030.png" alt="How blended is each meat category" class="ax-sig-img"/>
                </div>

                <div class="ax-sig">
                  <div class="ax-sig-watermark">02</div>
                  <div class="ax-sig-head">
                    <span class="ax-sig-num">02</span>
                    <div>
                      <div class="ax-sig-eyebrow">Signal</div>
                      <div class="ax-sig-title">Adoption follows formulation flexibility</div>
                    </div>
                  </div>
                  <p class="ax-sig-text">The analysis reveals a strong relationship between category structure and reformulation feasibility. Processed and structured formats
                    demonstrate significantly higher blending penetration than whole-cut or premium formats.</p>
                  <img src="/images/reports/balanced-protein/img-032.png" alt="Which categories are most blended" class="ax-sig-img"/>
                </div>

                <div class="ax-sig">
                  <div class="ax-sig-watermark">03</div>
                  <div class="ax-sig-head">
                    <span class="ax-sig-num">03</span>
                    <div>
                      <div class="ax-sig-eyebrow">Signal</div>
                      <div class="ax-sig-title">Retailers are becoming transition integrators</div>
                    </div>
                  </div>
                  <p class="ax-sig-text">Balanced protein implementation is strongly concentrated within private-label systems, reinforcing the role retailers increasingly play as
                    transition integrators through procurement, sourcing and reformulation strategies.</p>
                  <img src="/images/reports/balanced-protein/img-033.png" alt="Private-label vs brands" class="ax-sig-img"/>
                </div>

                <div class="ax-sig">
                  <div class="ax-sig-watermark">04</div>
                  <div class="ax-sig-head">
                    <span class="ax-sig-num">04</span>
                    <div>
                      <div class="ax-sig-eyebrow">Signal</div>
                      <div class="ax-sig-title">Ingredient systems reveal strategic priorities</div>
                    </div>
                  </div>
                  <p class="ax-sig-text">The ingredient analysis reveals increasing diversification across protein systems. Ingredients such as fava bean protein and rapeseed
                    protein appear disproportionately concentrated within blended formulations, suggesting emerging experimentation around sourcing flexibility and regional
                    proteins.</p>
                  <img src="/images/reports/balanced-protein/img-035.png" alt="Plant proteins in blended vs plant-based products" class="ax-sig-img"/>
                  <img src="/images/reports/balanced-protein/img-036.png" alt="Why each category is blended" class="ax-sig-img ax-sig-img--sep"/>
                </div>

                <div class="ax-sig">
                  <div class="ax-sig-watermark">05</div>
                  <div class="ax-sig-head">
                    <span class="ax-sig-num">05</span>
                    <div>
                      <div class="ax-sig-eyebrow">Signal</div>
                      <div class="ax-sig-title">Mainstream adaptation scales through invisible reformulation</div>
                    </div>
                  </div>
                  <p class="ax-sig-text">Most balanced reformulation remains relatively invisible to consumers. Only a very small minority of products explicitly communicate
                    hybridization, while the vast majority continue to rely on familiar category conventions and back-of-pack disclosure.</p>
                </div>

                <div class="ax-sig">
                  <div class="ax-sig-watermark">06</div>
                  <div class="ax-sig-head">
                    <span class="ax-sig-num">06</span>
                    <div>
                      <div class="ax-sig-eyebrow">Signal</div>
                      <div class="ax-sig-title">Balanced protein aligns multiple pressures simultaneously</div>
                    </div>
                  </div>
                  <p class="ax-sig-text">Balanced formulations increasingly function as multifunctional optimization systems rather than isolated sustainability products.</p>
                  <img src="/images/reports/balanced-protein/img-038.png" alt="Blended products deliver more protein per euro" class="ax-sig-img"/>
                </div>

              </div>

              <!-- Appendix conclusion -->
              <div class="fl-takeaway mt-6">
                <p class="text-xs font-bold fl-blue mb-2 uppercase tracking-widest">Conclusion</p>
                <p class="text-body text-gray-600 leading-relaxed">The Dutch retail analysis suggests that the protein transition is increasingly evolving through gradual
                  reformulation integrated inside mainstream food systems rather than through isolated replacement categories alone. Balanced protein therefore matters not only
                  because of the products themselves, but because the retail signals reveal how large-scale food-system adaptation may actually become operationally scalable in
                  practice: incrementally, pragmatically and through lower-friction integration pathways embedded within existing retail, procurement and consumption systems.</p>
              </div>

              <!-- Transition Monitor -->
              <div class="fl-infographic anim-fade-up mt-6">
                <img src="/images/reports/balanced-protein/img-043.png" alt="Balanced Protein Transition Monitor" class="w-full"/>
              </div>
            </div>
          </div>
        </section>

        <!-- About -->
        <section id="about" class="py-20 fl-section scroll-mt-16 anim-fade-up">
          <div class="fl-container space-y-8">
            <SectionHeader number="" title="ABOUT THIS REPORT"/>
            <div class="space-y-5 leading-relaxed text-body fl-blue">
              <p>This report was developed collaboratively by <strong>DataScouts</strong> and <strong>Foodvalley NL</strong> as part of the broader <strong>Foodleap</strong>
                initiative.</p>
              <p>The analysis combines continuous ecosystem monitoring, innovation tracking, and expert interpretation to help decision makers better understand the evolving
                dynamics
                of food system transitions.</p>
              <p>AI technologies were used to support large-scale signal processing, article clustering, and summarization workflows. Final analysis, synthesis, and strategic
                interpretation remain human-led.</p>
              <p>The report is intended for innovation leaders, R&amp;D teams, strategy professionals, ecosystem organisations, and policymakers navigating structural shifts within
                the food industry.</p>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                <div class="space-y-3">
                  <p class="font-bold fl-blue">About Foodleap</p>
                  <p class="text-sm text-gray-600">Foodleap is a global ecosystem intelligence platform designed to accelerate food system transitions. Foodleap combines continuous
                    market monitoring, ecosystem mapping, transition reporting, and collaborative intelligence to help organisations understand emerging opportunities, risks,
                    technologies, and ecosystem dynamics shaping the future of food.</p>
                  <p class="text-sm text-gray-600">The platform connects corporates, startups, researchers, investors, transition organisations, and policymakers around shared
                    transition challenges in the fields of Protein diversification, Healthy &amp; functional food, Nature-positive food systems incl. circular &amp; regenerative
                    agriculture as well as Food upcycling &amp; waste valorisation.</p>
                  <p class="text-sm text-gray-600">Foodleap combines AI-supported ecosystem monitoring with expert-driven analysis to identify, cluster, and interpret relevant
                    developments across the global food system.</p>
                  <p class="text-sm text-gray-600">By combining AI-supported intelligence with ecosystem collaboration, Foodleap aims to help organisations move from fragmented
                    signals to coordinated action.</p>
                </div>
                <div class="space-y-3">
                  <p class="font-bold fl-blue">About DataScouts</p>
                  <p class="text-sm text-gray-600">DataScouts is an ecosystem intelligence company that helps organisations navigate complex transitions through continuous market
                    monitoring, stakeholder mapping, and AI-supported intelligence workflows.</p>
                  <p class="text-sm text-gray-600">By combining data, ecosystem thinking, and strategic analysis, DataScouts supports corporates, public organisations, clusters,
                    and
                    innovation ecosystems in understanding emerging trends, identifying opportunities, and accelerating decision-making in rapidly evolving markets.</p>
                  <p class="text-sm text-gray-600">Its work focuses on building collective intelligence around major societal and industrial transitions, including food systems,
                    sustainability, circularity, health, and digital transformation.</p>
                </div>
                <div class="space-y-3">
                  <p class="font-bold fl-blue">About Foodvalley</p>
                  <p class="text-sm text-gray-600">Foodvalley NL is an independent food transition organisation dedicated to accelerating the transition towards a more sustainable
                    food system.</p>
                  <p class="text-sm text-gray-600">By connecting companies, knowledge institutions, governments, investors, and entrepreneurs, Foodvalley stimulates collaboration
                    and
                    innovation across the global agrifood ecosystem.</p>
                  <p class="text-sm text-gray-600">Foodvalley works on key transition themes including protein diversification, sustainable food production, health, and
                    regenerative
                    food systems, with the ambition to help shape a future-proof food system that balances environmental, economic, and societal impact.</p>
                </div>
              </div>

              <!-- Transition Intelligence section -->
              <div class="pt-6 border-t border-gray-100">
                <p class="font-bold fl-blue mb-4">About Transition Intelligence</p>
                <div class="space-y-4 text-gray-600">
                  <p>Much of the early protein-transition narrative was shaped around disruption: replacing meat, replacing dairy or replacing existing consumption patterns
                    altogether
                    through entirely new categories and technologies.</p>
                  <p>But transitions in complex systems rarely scale through disruption alone. Similar patterns can be observed across other industries undergoing large-scale
                    transformation. In sectors such as mobility and energy, early transition phases were initially driven by breakthrough innovation and category creation. Over
                    time,
                    however, the central challenge shifted from proving alternatives were possible toward integrating those alternatives into existing systems and
                    infrastructures.</p>
                  <p>Electric mobility, for example, initially focused on replacing combustion engines through standalone electric vehicles. As the market matured, scaling
                    increasingly depended on charging infrastructure, manufacturing integration, grid compatibility, interoperability and operational optimization.</p>
                  <p>The same pattern emerged in renewable energy, where early disruption around solar and wind technologies gradually evolved toward smart grids, storage systems,
                    sector integration and system-wide optimization.</p>
                  <p>The protein transition increasingly appears to follow a similar trajectory. The first wave demonstrated that alternative proteins could become commercially and
                    technologically viable. The emerging retail signals now suggest that the next challenge is no longer replacement, but integration: how new protein systems can
                    adapt pragmatically within existing retail structures, manufacturing systems, procurement models and everyday consumption habits.</p>
                  <p>Balanced protein is particularly relevant in this context because it reduces friction across multiple dimensions. Its importance therefore lies not only in the
                    products themselves, but in what they reveal about how food-system adaptation may actually scale in practice.</p>
                </div>
                <p class="font-semibold fl-blue mt-6 mb-3">Innovation and integration evolve together</p>
                <div class="space-y-4 text-gray-600">
                  <p>In many industries, early transition phases are often driven by breakthrough technologies, new entrants and radical experimentation. Over time, however, the
                    challenge gradually shifts from proving alternatives can exist toward integrating those alternatives into existing infrastructures, supply systems, operational
                    models and everyday behaviours.</p>
                  <p>This pattern can be observed across multiple transitions.</p>
                  <p>In mobility, early innovation initially focused on replacing combustion engines through entirely new vehicle technologies. As the market matured, however,
                    innovation increasingly shifted toward the integration layer itself: charging infrastructure, smart-grid coordination, battery management systems,
                    interoperability
                    standards, fleet optimization, software orchestration and supply-chain redesign became critical innovation domains enabling large-scale adoption.</p>
                  <p>In energy, early renewable technologies created the technological foundation for transition, but scaling increasingly depended on new forms of system-level
                    innovation around storage systems, balancing mechanisms, grid intelligence, decentralized energy coordination and integration into broader energy
                    infrastructures.</p>
                </div>
              </div>

              <div class="pt-6 border-t border-gray-100 text-xs text-gray-400">
                <p class="font-semibold mb-1">Copyright &amp; Usage</p>
                <p>© 2026 Foodleap. All rights reserved. This report was developed collaboratively by DataScouts and Foodvalley NL as part of the Foodleap initiative. This
                  publication may not be reproduced, distributed, or transmitted in any form or by any means without prior written permission from the authors, except for brief
                  quotations with appropriate attribution.</p>
              </div>
            </div>
          </div>
        </section>

      </template><!-- /hasFullAccess -->

    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, h } from 'vue'

  const SectionHeader = defineComponent({
    props: ['number', 'title'],
    render() {
      return h('div', { class: 'section-header-block' }, [
        this.number ? h('div', { class: 'section-number-badge' }, this.number) : null,
        h('div', { class: 'section-header-content' }, [
          h('h2', { class: 'section-title-text' }, this.title),
          h('div', { class: 'section-title-bar' }),
        ]),
      ])
    },
  })

  const SignalBox = defineComponent({
    props: ['number', 'title', 'observe', 'reveals', 'implication', 'appendix'],
    render() {
      const S = {
        card: 'border:1px solid #D9E2DA;border-radius:14px;overflow:hidden;box-shadow:0 4px 24px rgba(33,36,74,0.09);background:#fff;',
        header: 'background:#21244A;padding:28px 36px;display:flex;align-items:center;gap:28px;',
        num: 'font-family:Inter,system-ui,sans-serif;font-size:60px;font-weight:800;color:#5CC61E;line-height:1;flex-shrink:0;opacity:0.85;',
        headerRight: 'display:flex;flex-direction:column;gap:5px;',
        eyebrow: 'font-family:Inter,system-ui,sans-serif;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.14em;color:rgba(92,198,30,0.65);',
        title: 'font-family:Inter,system-ui,sans-serif;font-size:18px;font-weight:700;color:#fff;margin:0;line-height:1.3;',
        rowBase: 'padding:22px 36px 22px 32px;border-bottom:1px solid #f0f2f0;border-left:4px solid ',
        labelBase: 'font-family:Inter,system-ui,sans-serif;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:7px;color:',
        textBase: 'font-family:Georgia,serif;font-size:14px;line-height:1.75;margin:0;',
        appendix: 'padding:11px 36px;background:#f8f9fb;border-top:1px solid #D9E2DA;font-size:11px;color:#9ca3af;font-style:italic;',
      }
      const row = (label: string, text: string, border: string, labelColor: string, textColor: string, rowBg = '', bold = false) =>
        h('div', { style: S.rowBase + border + ';' + rowBg }, [
          h('div', { style: S.labelBase + labelColor + ';' }, label),
          h('p', { style: S.textBase + `color:${textColor};` + (bold ? 'font-weight:600;' : '') }, text),
        ])
      return h('div', { style: S.card }, [
        h('div', { style: S.header }, [
          h('div', { style: S.num }, this.number),
          h('div', { style: S.headerRight }, [
            h('div', { style: S.eyebrow }, 'Signal'),
            h('h3', { style: S.title }, this.title),
          ]),
        ]),
        h('div', {}, [
          row('What we observe', this.observe, '#9ca3af', '#9ca3af', '#374151'),
          row('What this reveals', this.reveals, '#21244A', '#21244A', '#374151'),
          row('Strategic implication', this.implication, '#5CC61E', '#5CC61E', '#21244A', 'background:#f5faf2;', true),
        ]),
        this.appendix ? h('div', { style: S.appendix }, this.appendix) : null,
      ])
    },
  })

  const ImplicationCard = defineComponent({
    props: ['title', 'rows', 'accent'],
    render() {
      const rows = this.rows as Array<{ label: string; text: string; color: string }>
      const accent = (this.accent as string) || '#5CC61E'
      return h('div', {
        style: `background:#fff;border:1px solid #e5e7eb;border-top:3px solid ${accent};border-radius:12px;display:flex;flex-direction:column;box-shadow:0 2px 12px rgba(0,0,0,0.06);`,
      }, [
        h('div', { style: 'padding:18px 22px 14px;border-bottom:1px solid #f3f4f6;' }, [
          h('p', { style: 'font-family:Inter,system-ui,sans-serif;font-size:14px;font-weight:700;color:#21244A;margin:0;line-height:1.3;' }, this.title),
        ]),
        h('div', { style: 'flex:1;' }, rows.map((r: { label: string; text: string; color: string }, i: number) =>
          h('div', {
            style: `padding:14px 22px;` + (i < rows.length - 1 ? 'border-bottom:1px solid #f3f4f6;' : ''),
          }, [
            h('span', { style: `display:inline-block;font-family:Inter,system-ui,sans-serif;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.12em;color:${r.color};background:${r.color}18;padding:2px 8px;border-radius:4px;margin-bottom:7px;` }, r.label),
            h('p', { style: 'font-family:Georgia,serif;font-size:14px;line-height:1.72;color:#4b5563;margin:0;' }, r.text),
          ])
        )),
      ])
    },
  })

  export default defineComponent({
    name: 'DeepDiveReportBalancedProtein',
    components: { SectionHeader, SignalBox, ImplicationCard },
    computed: {
      hasFullAccess(): boolean {
        return (this as any).$store.getters.hasActiveTransitionReportSubscription
          || (this as any).$store.getters.hasTransitionReportPaidAccess
      },
      hasTrialOnly(): boolean {
        return (this as any).$store.getters.hasActiveTransitionReportTrial
          && !(this as any).$store.getters.hasActiveTransitionReportSubscription
          && !(this as any).$store.getters.hasTransitionReportPaidAccess
      },
      activeStoryIndex(): number {
        const slides = (this as any).storySlides
        const count = slides.length
        const progress = (this as any).storyScrollProgress
        return Math.min(Math.floor(progress * count), count - 1)
      },
    },
    data() {
      return {
        scrollProgress: 0,
        kpiDisplayValues: ['0', '0', '0%', '0'],
        activeSection: 'executive-summary',
        navOnDark: false,
        progressSections: [
          { id: 'executive-summary', label: 'Executive Summary' },
          { id: 'section-1', label: 'Retail Signals' },
          { id: 'section-2', label: 'Replacement Limits' },
          { id: 'section-3', label: 'Category Dynamics' },
          { id: 'section-4', label: 'Industry Implications' },
          { id: 'section-5', label: 'Foodservice' },
          { id: 'section-6', label: 'Conclusion' },
          { id: 'appendix', label: 'Appendix' },
        ],
        heroRevealed: false,
        cursorX: 0,
        cursorY: 0,
        chartVisible: false,
        storyScrollProgress: 0,
        appendixOpen: [true, false, false, false, false, false],
        scrollContainerH: 0, // set in mounted to sc.clientHeight
        signalTitles: [
          'Balanced protein is entering mainstream retail portfolios',
          'Adoption follows formulation flexibility',
          'Retailers are becoming transition integrators',
          'Ingredient systems reveal strategic priorities',
          'Mainstream adaptation scales through invisible reformulation',
          'Balanced protein aligns multiple pressures simultaneously',
        ],
        storySlides: [
          {
            stat: '4.7%',
            unit: 'of food portfolio',
            title: 'Balanced protein has entered the mainstream',
            body: 'Analysis of 7,714 food products across Albert Heijn, Jumbo and SPAR identified 362 intentionally blended products combining animal and plant-based proteins — 4.7% of the total analysed portfolio. In processed meat categories, balanced formulations represent approximately 17.5% of comparable assortments.',
          },
          {
            stat: '~40%',
            unit: 'EU-only protein sourcing',
            title: 'Ingredient systems reveal strategic priorities',
            body: 'Nearly 40% of blended products already rely exclusively on EU-sourced protein inputs. Ingredients such as fava bean protein, rapeseed protein and pea protein are diversifying beyond soy — reflecting priorities around sourcing resilience, affordability and operational flexibility.',
          },
          {
            stat: '46%',
            unit: 'kroket category',
            title: 'Adoption follows formulation flexibility',
            body: 'Nearly half of all kroketten now contain balanced protein formulations (n=31), with frikandel at 46% (n=17) and nugget at 44% (n=12) close behind. Categories with high processing flexibility show significantly higher blending rates than whole-cut or premium formats.',
          },
          {
            stat: '+6%',
            unit: 'more protein per euro',
            title: 'Balanced protein aligns multiple pressures simultaneously',
            body: 'Blended products deliver a median 9.1g of protein per €1 spent versus 8.6g for conventional equivalents — 6% more nutritional value at comparable price points. Plant-based alternatives deliver only 5.7g per €1, making balanced protein a uniquely affordable and nutritious transition pathway.',
          },
        ],
        lockedSections: [
          '1 — Retail Signals Reveal a New Reformulation Logic',
          '2 — Category Dynamics: Where Hybridisation Is Taking Hold',
          '3 — Ingredient Systems and Operational Feasibility',
          '4 — Industry Implications',
          '5 — Foodservice Dynamics',
          '6 — Conclusion & Strategic Outlook',
          'Appendix — Full Signal Data',
        ],
        signals: [
          {
            observe: 'Analysis of 7,714 food products across Albert Heijn, Jumbo and SPAR identified 362 intentionally blended products combining animal and plant-based proteins within the same formulation architecture. At total portfolio level, blended products still represent a minority position, accounting for 4.7% of the analyzed food portfolio. However, when focusing on the 33 meat product families analyzed in detail, balanced formulations represent approximately 17.5% of comparable assortments. Balanced protein products are no longer limited to isolated shelves or startup experimentation. They increasingly appear across familiar product categories incl. burgers, sausages, meatballs, cold cuts and convenience foods.',
            reveals: 'Balanced protein has entered an early implementation phase inside mainstream retail portfolios. Hybrid formulations are getting integrated into existing category structures rather than remaining confined to separate alternative-protein segments.',
            implication: 'The protein transition increasingly evolves through gradual reformulation inside mainstream product portfolios rather than through entirely separate replacement categories alone.',
          },
          {
            observe: 'Balanced protein adoption is highly uneven across product categories. The strongest penetration appears in processed and structured formats: 33% of frikandels, 24% of meatballs, and 15% of burgers contain blended formulations. By contrast, only 4% of premium filet products contain blended formulations. The category distribution further reinforces this pattern. Sausages account for 32% of identified balanced meat products, followed by burgers at 28.9% and meatballs at 10.3%.',
            reveals: 'Hybridization scales most easily in categories where formulation flexibility already exists. Processed and structured products already rely heavily on ingredient systems to shape texture, flavour, juiciness and consistency. This creates operational room to integrate plant proteins, fibres and alternative ingredients without fundamentally altering consumer expectations. Whole-cut and premium formats remain highly dependent on texture identity, visual appearance and sensory precision.',
            implication: 'The key bottlenecks for food-system adaptation are category compatibility and implementation feasibility. Innovation scales faster where friction remains manageable operationally, commercially, and behaviourally.',
          },
          {
            observe: 'Balanced protein implementation in Dutch retail is strongly concentrated within private-label systems, reflecting the dominant role retailers already play in mainstream meat categories. The analysis also reveals clear differentiation across ingredient systems. Ingredients such as fava bean protein, rapeseed corn, corn-based systems, fibres and vegetable inclusions appear across different balanced formulations, each supporting different objectives such as texture optimization, nutritional improvement, sourcing flexibility, affordability or clean-label positioning. At the same time, many of the enabling ingredient systems behind these reformulations continue to originate from ingredient suppliers, startups and food-technology innovators operating upstream in the value chain.',
            reveals: 'Balanced protein increasingly behaves like a portfolio adaptation strategy embedded within retailer-controlled systems. Implementation appears selective and pragmatic, focused on categories and ingredient combinations where reformulation can scale without disrupting pricing structures, operational continuity or consumer familiarity. The diversification across ingredient systems suggests that the transition is moving beyond single-protein replacement logic toward more multifunctional formulation architectures balancing operational, nutritional and sustainability objectives simultaneously.',
            implication: 'Retailers increasingly play an orchestration role through procurement, portfolio management and private-label implementation. At the same time, ingredient suppliers and innovation ecosystems remain essential in expanding the range of functional ingredients, protein systems and reformulation capabilities available to manufacturers and retailers. The transition increasingly depends on coordination between upstream ingredient innovation and downstream portfolio integration.',
          },
          {
            observe: 'The most common non-animal ingredients identified across balanced products include: fava bean protein, pea protein, vegetables, sugar beet fibre, chickpeas, rapeseed protein, seaweed, fungi, and soy-based systems. Nearly 40% of blended products already rely on EU-only protein sourcing. The analysis further shows major variation in formulation intensity: all identified blended products average 10.5% non-animal ingredients; explicitly communicated balanced products average 19.5%; while back-of-pack-only formulations average 5.8%.',
            reveals: 'Ingredient systems increasingly reflect broader strategic priorities around sourcing resilience, nutritional optimization, operational functionality, affordability, and formulation flexibility. Balanced protein is therefore evolving toward more multifunctional formulation architectures.',
            implication: 'Ingredients increasingly function as strategic infrastructure rather than simple recipe components. The next phase of food innovation may depend less on one dominant protein source and more on the ability to combine multiple ingredient systems flexibly across different operational and nutritional objectives.',
          },
          {
            observe: 'Major differences in how balanced products communicate hybridization to consumers. Products with explicit front-of-pack communication contain significantly higher levels of non-animal ingredients than products where reformulation remains largely invisible through back-of-pack-only disclosure. At the same time, many balanced products continue to rely primarily on familiar positioning cues such as premium quality, regionality, naturalness, craftsmanship and / or existing category naming conventions.',
            reveals: 'Mainstream adaptation currently appears to scale more easily through gradual reformulation. Rather than creating entirely separate "hybrid" categories, retailers and manufacturers increasingly integrate reformulation quietly inside products consumers already understand and purchase regularly.',
            implication: 'Food-system transition may not scale primarily through lower-friction reformulation pathways embedded inside familiar consumption patterns and existing retail categories.',
          },
          {
            observe: 'Compared to non-balanced reference products, the analysis identified lower average consumer prices, improved nutritional composition, similar or slightly improved Nutri-Scores, and increased formulation flexibility across many balanced products.',
            reveals: 'Balanced protein is gaining momentum not because it solves one isolated problem, but because it helps reduce pressure across multiple dimensions simultaneously incl. affordability, sourcing resilience, nutritional optimization, operational continuity, and sustainability ambitions.',
            implication: 'Balanced protein functions as a practical retail mechanism for improving the sustainability and nutritional profile of mainstream portfolios while maintaining commercial viability. Balanced protein reformulation allows retailers to integrate transition objectives directly into high-volume everyday products without fundamentally disrupting existing consumer behaviour or category performance.',
          },
        ],
        implication_rows: [
          // 4.1 Farmers
          [
            {
              label: 'Transition pressure',
              text: 'Growing interest in protein diversification, regional sourcing and supply-chain resilience is gradually increasing demand for alternative crop systems alongside traditional livestock production. At the same time, agricultural producers operate within long investment cycles, volatile commodity markets and highly constrained economic realities, making rapid transition difficult.',
              color: '#ef4444'
            },
            {
              label: 'Core tension',
              text: 'The value captured through reformulation often concentrates downstream in manufacturing, formulation and retail systems, while farmers remain exposed to commodity pricing pressure, investment uncertainty and evolving sourcing requirements.',
              color: '#f59e0b'
            },
            {
              label: 'Emerging opportunity',
              text: 'Balanced protein creates a different scaling pathway for protein crops. Because plant ingredients integrate directly into high-volume meat categories, demand for legumes, fibres and alternative protein crops can potentially scale alongside existing consumption systems. This may create new market opportunities for crops such as fava bean, pea and rapeseed systems integrated into mainstream food manufacturing.',
              color: '#5CC61E'
            },
            {
              label: 'Strategic implication',
              text: 'The growth of balanced protein may gradually expand demand for diversified protein crops, but long-term adoption will depend on whether supply chains, procurement systems and ingredient markets can create sufficient economic stability and scalable demand for primary producers.',
              color: '#21244A'
            },
          ],
          // 4.2 Ingredient Suppliers
          [
            {
              label: 'Transition pressure',
              text: 'Ingredient suppliers operate in a market where manufacturers and retailers increasingly require ingredients that can integrate into existing products. At the same time, many novel protein systems still face scaling challenges around cost, supply security, functionality and operational consistency.',
              color: '#ef4444'
            },
            {
              label: 'Core tension',
              text: 'No single ingredient system optimizes functionality, affordability, nutrition, scalability and clean-label expectations simultaneously. Ingredient suppliers increasingly need to support manufacturers not only with ingredients themselves, but also with formulation guidance, operational reliability and integration capabilities.',
              color: '#f59e0b'
            },
            {
              label: 'Emerging opportunity',
              text: 'Balanced protein creates new integration pathways for ingredient suppliers inside mainstream meat and convenience categories, integrating proteins, fibres and functional ingredients directly into high-volume reformulation systems. This expands potential demand for ingredients such as fava bean protein, rapeseed protein, fibres, fungi and functional blends capable of supporting texture, nutritional quality, water binding, sourcing flexibility and cost optimization.',
              color: '#5CC61E'
            },
            {
              label: 'Strategic implication',
              text: 'Competitive advantage increasingly shifts toward ingredient systems capable of integrating flexibly into scalable manufacturing and retail realities.',
              color: '#21244A'
            },
          ],
          // 4.3 Meat & Protein Manufacturers
          [
            {
              label: 'Transition pressure',
              text: 'Meat manufacturers face growing pressure around sustainability, sourcing volatility and changing consumer expectations, while continuing to operate inside highly price-sensitive and operationally complex retail environments. Retailers increasingly expect suppliers to contribute to nutritional improvement and sustainability targets without compromising affordability, manufacturability or mainstream consumer acceptance.',
              color: '#ef4444'
            },
            {
              label: 'Core tension',
              text: 'Manufacturers must continuously balance cost, texture, nutritional quality and sourcing stability within highly competitive retail environments. Many emerging ingredient systems still face limitations around pricing, supply consistency and operational scalability.',
              color: '#f59e0b'
            },
            {
              label: 'Emerging opportunity',
              text: 'Balanced protein creates a pragmatic transition pathway for meat manufacturers. Rather than requiring complete replacement of existing production systems, reformulation allows manufacturers to gradually integrate plant proteins, fibres and alternative ingredients into familiar products and established manufacturing infrastructures. This is particularly relevant in processed categories such as burgers, sausages and meatballs.',
              color: '#5CC61E'
            },
            {
              label: 'Strategic implication',
              text: 'Manufacturers capable of integrating new ingredient systems pragmatically into existing production environments may be best positioned for the next phase of transition. Competitive advantage increasingly depends on the ability to operationalize reformulation at mainstream scale.',
              color: '#21244A'
            },
          ],
          // 4.4 Other Food Manufacturers
          [
            {
              label: 'Transition pressure',
              text: 'Manufacturers outside traditional meat production systems may face growing pressure to respond to changing nutritional trends and retailer reformulation targets.',
              color: '#ef4444'
            },
            {
              label: 'Core tension',
              text: 'Many manufacturers operate outside the dominant meat production and procurement ecosystems currently driving much of the balanced protein implementation. This may create challenges around operational integration.',
              color: '#f59e0b'
            },
            {
              label: 'Emerging opportunity',
              text: 'The growing diversification of ingredient systems creates opportunities for food manufacturers active in adjacent categories such as prepared meals, convenience foods, sauces, snacks and functional nutrition. As reformulation becomes increasingly multifunctional, opportunities emerge for integrating protein diversification, fibre enrichment and nutritional optimization into broader product portfolios.',
              color: '#5CC61E'
            },
            {
              label: 'Strategic implication',
              text: 'As reformulation logic expands beyond meat categories alone, opportunities emerge for integrating multifunctional food architectures combining nutrition, convenience, satiety and operational scalability across a broader range of everyday products.',
              color: '#21244A'
            },
          ],
          // 4.5 Retailers
          [
            {
              label: 'Transition pressure',
              text: 'Retailers increasingly operate at the intersection of economic pressure, nutritional expectations, sustainability ambitions, sourcing volatility, and category performance. Consumers increasingly expect all of these priorities to be balanced simultaneously.',
              color: '#ef4444'
            },
            {
              label: 'Core tension',
              text: 'Retailers must balance consumer behaviour, pricing pressure, operational simplicity, supplier coordination and sustainability targets within highly competitive market conditions.',
              color: '#f59e0b'
            },
            {
              label: 'Emerging opportunity',
              text: 'Retailers integrate balanced protein adaptations directly into mainstream, high-volume categories through private-label systems, procurement strategies and gradual reformulation pathways without requiring abrupt category disruption.',
              color: '#5CC61E'
            },
            {
              label: 'Strategic implication',
              text: 'Retailers become transition orchestrators through procurement, private label, reformulation and portfolio management.',
              color: '#21244A'
            },
          ],
          // 4.6 Food Service Operators
          [
            {
              label: 'Transition pressure',
              text: 'Growing pressure from institutional clients, public procurement frameworks and corporate sustainability expectations is gradually increasing the need for foodservice operators to adapt menus, sourcing strategies and nutritional performance.',
              color: '#ef4444'
            },
            {
              label: 'Core tension',
              text: 'Many foodservice operators still struggle with margin pressure, waste management, staffing shortages, procurement complexity and limited operational visibility across their supply systems. Sustainability and nutritional reformulation remain secondary to day-to-day operational survival.',
              color: '#f59e0b'
            },
            {
              label: 'Emerging opportunity',
              text: 'Balanced formulations align relatively well with foodservice realities as they integrate easily into familiar menu structures, scalable kitchen operations and existing consumer routines, allowing for gradual menu adaptation.',
              color: '#5CC61E'
            },
            {
              label: 'Strategic implication',
              text: 'The transition in foodservice depends less on radical menu innovation and more on operationally simple solutions that help operators improve nutrition and sustainability without increasing kitchen complexity, waste or cost pressure.',
              color: '#21244A'
            },
          ],
          // 4.7 Consumers
          [
            {
              label: 'Transition pressure',
              text: 'Consumers are increasingly exposed to conflicting food narratives around health, sustainability, ultra-processed food, and affordability, while everyday food decisions remain driven primarily by habit, convenience and price.',
              color: '#ef4444'
            },
            {
              label: 'Core tension',
              text: 'Consumers expect food to remain affordable, tasty and trustworthy while food systems become more complex.',
              color: '#f59e0b'
            },
            {
              label: 'Emerging opportunity',
              text: 'Balanced protein allows adaptation to happen inside existing consumption routines rather than requiring consumers to fundamentally change eating behaviour — creating gradual nutritional and sustainability improvements without forcing engagement with complex transition narratives.',
              color: '#5CC61E'
            },
            {
              label: 'Strategic implication',
              text: 'The transition will scale most effectively through products that integrate naturally into existing routines, without consumers actively engaging in nutritional or sustainability trade-offs themselves.',
              color: '#21244A'
            },
          ],
        ],
        vcActors: [
          { name: 'Farmers & Primary Production', short: 'Farmers', accent: '#22c55e' },
          { name: 'Ingredient Suppliers', short: 'Ingredients', accent: '#f59e0b' },
          { name: 'Meat & Protein Manufacturers', short: 'Meat & Protein', accent: '#ef4444' },
          { name: 'Other Food Manufacturers', short: 'Other Mfrs', accent: '#3b82f6' },
          { name: 'Retailers', short: 'Retailers', accent: '#a855f7' },
          { name: 'Food Service Operators', short: 'Food Service', accent: '#f97316' },
          { name: 'Consumers', short: 'Consumers', accent: '#14b8a6' },
        ],
        categoryData: [
          { name: 'Kroket', pct: 46, n: 31 },
          { name: 'Frikandel', pct: 46, n: 17 },
          { name: 'Nugget', pct: 44, n: 12 },
          { name: 'Loempia', pct: 37, n: 7 },
          { name: 'Gehaktbal', pct: 33, n: 31 },
          { name: 'Filet american', pct: 33, n: 15 },
          { name: 'Slavink', pct: 30, n: 3 },
          { name: 'Braadworst', pct: 29, n: 33 },
          { name: 'Rookworst', pct: 27, n: 13 },
          { name: 'Kraaknoot', pct: 26, n: 10 },
          { name: 'Burger', pct: 24, n: 31 },
          { name: 'Chorizo', pct: 19, n: 7 },
          { name: 'Schnitzel', pct: 16, n: 17 },
          { name: 'Worst overig', pct: 12, n: 28 },
          { name: 'Pate/Leverworst', pct: 9, n: 10 },
          { name: 'Kip stukken', pct: 9, n: 15 },
          { name: 'Gehakt', pct: 9, n: 8 },
          { name: 'Shoarma/Kebab', pct: 7, n: 4 },
          { name: 'Salami', pct: 0, n: 4 },
        ],
        ingredientData: [
          { name: 'Generic plant protein', blended: 46, plantBased: 69 },
          { name: 'Fava bean protein', blended: 40, plantBased: '(small)' },
          { name: 'Wheat gluten', blended: 41, plantBased: 64 },
          { name: 'Soy protein', blended: 36, plantBased: 400 },
          { name: 'Rapeseed protein', blended: 35, plantBased: '(small)' },
          { name: 'Pea protein', blended: 26, plantBased: 47 },
          { name: 'Corn protein', blended: 18, plantBased: 13 },
          { name: 'Chickpea', blended: 7, plantBased: '(small)' },
          { name: 'Potato protein', blended: 6, plantBased: 8 },
        ],
      }
    },
    mounted() {
      // ── Scroll container ───────────────────────────────────────────────
      // body/html have overflow:hidden — actual page scroll is on .main-view
      const sc = (document.querySelector('.main-view') as HTMLElement) ?? document.documentElement
      this.scrollContainerH = sc.clientHeight
      document.documentElement.style.setProperty('--sc-h', sc.clientHeight + 'px')

      // How many px of 'el' have scrolled above sc's top edge
      const scrolledInto = (el: HTMLElement): number => {
        const scTop = sc.getBoundingClientRect().top
        const elTop = el.getBoundingClientRect().top
        return Math.max(0, scTop - elTop)
      }

      // ── Reading progress bar ───────────────────────────────────────────
      const updateProgress = () => {
        const scrollable = sc.scrollHeight - sc.clientHeight
        this.scrollProgress = scrollable > 0 ? Math.min(100, (sc.scrollTop / scrollable) * 100) : 0
      }
      sc.addEventListener('scroll', updateProgress, { passive: true })
      ;(this as any)._removeProgress = () => sc.removeEventListener('scroll', updateProgress)

      // ── Side nav contrast: flip the labels to white while a dark block is behind the nav ──
      const darkBlocksSelector = '.hero-section, .sticky-story, .signals-hscroll, .quote-block, .story-panels, .vc-section'
      const updateNavContrast = () => {
        const navEl = (this as any).$el.querySelector('.side-nav')

        if (!navEl) {
          return
        }

        // Sample at the nav's dot column: a dark block only counts when it actually extends behind the nav,
        // centered content blocks (e.g. quotes) that stop before the right edge should not flip the colors
        const navRect = navEl.getBoundingClientRect()
        const navX = navRect.right - 6
        const navMidY = window.innerHeight / 2

        this.navOnDark = Array.from((this as any).$el.querySelectorAll(darkBlocksSelector))
          .some((el: any) => {
            const rect = el.getBoundingClientRect()

            return rect.top <= navMidY && rect.bottom >= navMidY && rect.left <= navX && rect.right >= navX
          })
      }
      updateNavContrast()
      sc.addEventListener('scroll', updateNavContrast, { passive: true })
      ;(this as any)._removeNavContrast = () => sc.removeEventListener('scroll', updateNavContrast)

      // ── Scroll-triggered fade-up animations ───────────────────────────
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible')
            io.unobserve(e.target)
          }
        })
      }, { root: sc, threshold: 0.06, rootMargin: '0px 0px -40px 0px' })
      document.querySelectorAll('.anim-fade-up').forEach(el => io.observe(el))
      ;(this as any)._ioFadeUp = io

      // ── Active section tracker for side nav ───────────────────────────
      const sectionIo = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.activeSection = entry.target.id
        })
      }, { root: sc, rootMargin: '-15% 0px -70% 0px', threshold: 0 })
      document.querySelectorAll('section[id]').forEach(el => sectionIo.observe(el))
      ;(this as any)._sectionIo = sectionIo

      // ── Hero reveal on load ────────────────────────────────────────────
      setTimeout(() => {
        this.heroRevealed = true
      }, 100)

      // ── Cursor glow tracking on hero ───────────────────────────────────
      const heroEl = this.$refs.heroSection as HTMLElement
      if (heroEl) {
        const onMove = (e: MouseEvent) => {
          const rect = heroEl.getBoundingClientRect()
          this.cursorX = e.clientX - rect.left
          this.cursorY = e.clientY - rect.top
        }
        heroEl.addEventListener('mousemove', onMove)
        ;(this as any)._removeCursor = () => heroEl.removeEventListener('mousemove', onMove)
      }

      // ── Chart visibility observer ──────────────────────────────────────
      const chartEl = this.$refs.categoryChart as HTMLElement
      if (chartEl) {
        const chartIo = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            this.chartVisible = true
            chartIo.disconnect()
          }
        }, { root: sc, threshold: 0.2 })
        chartIo.observe(chartEl)
      }

      // Sticky story — scroll-driven track translation + left stat update
      const slideCount = this.storySlides.length
      const slides = this.storySlides
      ;(this as any)._lastStoryIdx = 0

      const updateStoryStats = (storyEl: HTMLElement, idx: number) => {
        const eyebrow = storyEl.querySelector('.sticky-stat-eyebrow') as HTMLElement | null
        const number = storyEl.querySelector('.sticky-stat-number') as HTMLElement | null
        const unit = storyEl.querySelector('.sticky-stat-unit') as HTMLElement | null
        if (!eyebrow || !number || !unit) return
        // Fade out → swap text → fade in
        eyebrow.style.opacity = '0'
        number.style.opacity = '0'
        unit.style.opacity = '0'
        setTimeout(() => {
          eyebrow.textContent = `Key finding ${idx + 1}`
          number.textContent = slides[idx].stat
          unit.textContent = slides[idx].unit
          eyebrow.style.opacity = '1'
          number.style.opacity = '1'
          unit.style.opacity = '1'
        }, 140)
      }

      const updateStory = () => {
        const storyEl = (this as any).$el.querySelector('.sticky-story') as HTMLElement | null
        const storyTrack = (this as any).$el.querySelector('.sticky-story-track') as HTMLElement | null
        if (!storyEl || !storyTrack) return
        const scrolled = scrolledInto(storyEl)
        const viewH = sc.clientHeight
        const totalScrollable = (slideCount - 1) * viewH
        if (totalScrollable <= 0) return
        const progress = Math.min(scrolled / totalScrollable, 1)

        storyTrack.style.transform = `translateY(-${progress * (slideCount - 1) * viewH}px)`

        const idx = Math.min(Math.floor(progress * slideCount), slideCount - 1)
        if (idx !== (this as any)._lastStoryIdx) {
          ;(this as any)._lastStoryIdx = idx
          updateStoryStats(storyEl, idx)
          storyEl.querySelectorAll('.sticky-dot').forEach((dot: Element, i: number) => dot.classList.toggle('active', i === idx))
        }
      }

      // Set initial active dot once DOM is ready
      setTimeout(() => {
        const storyEl = (this as any).$el.querySelector('.sticky-story') as HTMLElement | null
        storyEl?.querySelector('.sticky-dot')?.classList.add('active')
      }, 50)

      sc.addEventListener('scroll', updateStory, { passive: true })
      ;(this as any)._removeStory = () => sc.removeEventListener('scroll', updateStory)

      // ── Horizontal signal scroll ───────────────────────────────────────
      const signalCount = this.signals.length
      const updateSignals = () => {
        const hscroll = (this as any).$el.querySelector('.signals-hscroll') as HTMLElement | null
        const track = (this as any).$el.querySelector('.sh-track') as HTMLElement | null
        if (!hscroll || !track) return
        const scrolled = scrolledInto(hscroll)
        const viewH = sc.clientHeight
        const totalScrollable = (signalCount - 1) * viewH
        if (totalScrollable <= 0) return
        const progress = Math.min(scrolled / totalScrollable, 1)
        const firstCard = track.querySelector('.sh-card') as HTMLElement | null
        const cardStep = firstCard ? firstCard.offsetWidth + 20 : window.innerWidth * 0.44 + 20
        track.style.transform = `translateX(-${progress * (signalCount - 1) * cardStep}px)`
        const idx = Math.min(Math.floor(progress * signalCount), signalCount - 1)
        const counter = hscroll.querySelector('.sh-counter-current')
        if (counter) counter.textContent = String(idx + 1)
        hscroll.querySelectorAll('[data-sh-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === idx))
        const hint = (this as any).$refs.signalScrollHint as HTMLElement | null
        if (hint) {
          const scRect = sc.getBoundingClientRect()
          const hRect = hscroll.getBoundingClientRect()
          const pinActive = hRect.top <= scRect.top && hRect.bottom > scRect.top
          hint.style.opacity = (pinActive && progress < 0.08) ? '1' : '0'
        }
      }
      sc.addEventListener('scroll', updateSignals, { passive: true })
      ;(this as any)._removeSignals = () => sc.removeEventListener('scroll', updateSignals)

      // ── Value chain scroll ─────────────────────────────────────────────
      const VC_CHAIN_H = 76
      const vcCount = (this as any).vcActors.length
      let lastVcIdx = -1

      const updateVCActor = (vcEl: HTMLElement, idx: number) => {
        const actor = (this as any).vcActors[idx]
        const numEl = vcEl.querySelector('.vc-actor-num') as HTMLElement | null
        const nameEl = vcEl.querySelector('.vc-actor-name') as HTMLElement | null
        const ofEl = vcEl.querySelector('.vc-actor-of') as HTMLElement | null
        if (!numEl || !nameEl) return
        numEl.style.opacity = '0'
        nameEl.style.opacity = '0'
        setTimeout(() => {
          numEl.textContent = String(idx + 1).padStart(2, '0')
          numEl.style.color = actor.accent
          nameEl.textContent = actor.name
          if (ofEl) ofEl.textContent = `Actor ${idx + 1} of ${vcCount}`
          numEl.style.opacity = '1'
          nameEl.style.opacity = '1'
        }, 140)
        vcEl.querySelectorAll('[data-vc-node]').forEach((node, i) => {
          const n = node as HTMLElement
          const accent = n.dataset.accent || '#5CC61E'
          const dot = n.querySelector('.vc-node-dot') as HTMLElement | null
          const label = n.querySelector('.vc-node-label') as HTMLElement | null
          if (dot) {
            dot.style.background = i === idx ? accent : (i < idx ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.08)')
            dot.style.transform = i === idx ? 'scale(1.5)' : 'scale(1)'
            dot.style.boxShadow = i === idx ? `0 0 0 4px ${accent}35` : 'none'
          }
          if (label) {
            label.style.color = i === idx ? 'rgba(255,255,255,0.9)' : (i < idx ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.18)')
          }
        })
        vcEl.querySelectorAll('.vc-connector').forEach((conn, i) => {
          ;(conn as HTMLElement).style.background = i < idx ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.07)'
        })
        vcEl.querySelectorAll('[data-vc-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === idx))
      }

      const updateVC = () => {
        const vcEl = (this as any).$el.querySelector('.vc-section') as HTMLElement | null
        const vcTrack = (this as any).$el.querySelector('.vc-track') as HTMLElement | null
        if (!vcEl || !vcTrack) return
        const scrolled = scrolledInto(vcEl)
        const viewH = sc.clientHeight
        const totalScrollable = (vcCount - 1) * viewH
        if (totalScrollable <= 0) return
        const progress = Math.min(scrolled / totalScrollable, 1)
        const slideH = viewH - VC_CHAIN_H
        vcTrack.style.transform = `translateY(-${progress * (vcCount - 1) * slideH}px)`
        const idx = Math.min(Math.floor(progress * vcCount), vcCount - 1)
        if (idx !== lastVcIdx) {
          lastVcIdx = idx
          updateVCActor(vcEl, idx)
        }
      }
      sc.addEventListener('scroll', updateVC, { passive: true })
      ;(this as any)._removeVC = () => sc.removeEventListener('scroll', updateVC)
      // initialise state so first actor is highlighted on load
      updateVC()

      // ── KPI count-up animation ─────────────────────────────────────────
      const kpiGrid = (this as any).$el.querySelector('.fl-kpi-grid')
      if (kpiGrid) {
        const kpiTargets = [7714, 362, 17.5, 3]
        const kpiSuffixes = ['', '', '%', '']
        const kpiIo = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            kpiIo.disconnect()
            kpiTargets.forEach((target, i) => {
              (this as any).animateCounter(i, target, kpiSuffixes[i])
            })
          }
        }, { root: sc, threshold: 0.5 })
        kpiIo.observe(kpiGrid)
      }
    },
    beforeUnmount() {
      ;(this as any)._removeProgress?.()
      ;(this as any)._removeNavContrast?.()
      ;(this as any)._ioFadeUp?.disconnect()
      ;(this as any)._sectionIo?.disconnect()
      ;(this as any)._removeCursor?.()
      ;(this as any)._removeStory?.()
      ;(this as any)._removeSignals?.()
      ;(this as any)._removeVC?.()
    },
    methods: {
      isSectionLocked(id: string): boolean {
        return !(this as any).hasFullAccess && id !== 'executive-summary'
      },
      scrollToSection(id: string) {
        if ((this as any).isSectionLocked(id)) return
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      },
      animateCounter(index: number, end: number, suffix: string) {
        const duration = 1800
        const isDecimal = end % 1 !== 0
        const startTime = performance.now()
        const tick = (now: number) => {
          const p = Math.min((now - startTime) / duration, 1)
          const eased = 1 - Math.pow(1 - p, 3)
          const val = end * eased
          this.kpiDisplayValues[index] = isDecimal
            ? val.toFixed(1) + suffix
            : Math.floor(val).toLocaleString() + suffix
          if (p < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      },
      async exportPDF() {
        const html2pdf = (await import('html2pdf.js')).default
        await html2pdf().set({
          margin: [12, 14],
          filename: 'deep-dive-report-balanced-protein.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        }).from(document.getElementById('report-content')).save()
      },
    },
  })
</script>

<style scoped>
  /* ─── Brand tokens ─────────────────────────────────────────────── */
  .fl-green {
    color: #5CC61E;
  }

  .fl-blue {
    color: #21244A;
  }

  .bg-fl-blue {
    background-color: #21244A;
  }

  .bg-fl-green {
    background-color: #5CC61E;
  }

  .bg-fl-light {
    background-color: #f5faf2;
  }

  .bg-fl-quote {
    background-color: #eef8e8;
  }

  .border-fl-green {
    border-color: #5CC61E;
  }

  .border-fl-grey {
    border-color: #D9E2DA;
  }

  /* ─── Root ──────────────────────────────────────────────────────── */
  .report-root {
    font-family: Georgia, 'Times New Roman', serif;
    color: #21244A;
    line-height: 1.85;
    font-size: 17px;
    background: #fff;
  }

  /* ─── Layout ────────────────────────────────────────────────────── */
  .fl-container {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 48px;
  }

  /* Alternating section backgrounds */
  .fl-section {
    background: #fff;
    position: relative;
  }

  .fl-section-alt {
    background: linear-gradient(180deg, #f8f9fb 0%, #f5f7f5 100%);
    position: relative;
  }

  /* ─── Sticky header ─────────────────────────────────────────────── */
  .header-bar {
    position: relative;
    border-top: none;
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    background: rgba(255, 255, 255, 0.88) !important;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
  }

  .reading-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    background: linear-gradient(90deg, #5CC61E 0%, #21244A 100%);
    transition: width 0.15s linear;
    pointer-events: none;
    z-index: 10;
  }

  .fl-blue-btn {
    color: #21244A;
    font-family: inherit;
  }

  .fl-nav-link {
    color: #21244A;
    text-decoration: none;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    transition: color 0.15s;
    font-size: 15px;
  }

  .fl-nav-link:hover {
    color: #5CC61E;
  }

  /* ─── Hero ───────────────────────────────────────────────────────── */
  .hero-section {
    position: relative;
    background: linear-gradient(160deg, #181b3e 0%, #21244A 40%, #1c2b1c 100%);
    overflow: hidden;
    padding: 120px 0 100px;
  }

  .hero-blob {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(80px);
  }

  .hero-blob-1 {
    width: 800px;
    height: 800px;
    background: radial-gradient(circle, rgba(92, 198, 30, 0.12) 0%, transparent 70%);
    top: -300px;
    right: -200px;
    animation: blobFloat 14s ease-in-out infinite;
  }

  .hero-blob-2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(112, 193, 255, 0.08) 0%, transparent 70%);
    bottom: -150px;
    left: -80px;
    animation: blobFloat 18s ease-in-out infinite reverse;
    animation-delay: -6s;
  }

  .hero-blob-3 {
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(92, 198, 30, 0.06) 0%, transparent 70%);
    top: 50%;
    left: 35%;
    animation: blobFloat 11s ease-in-out infinite;
    animation-delay: -3s;
  }

  @keyframes blobFloat {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(40px, -40px) scale(1.06);
    }
    66% {
      transform: translate(-25px, 25px) scale(0.94);
    }
  }

  /* Hero cursor glow */
  .hero-cursor-glow {
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(92, 198, 30, 0.08) 0%, transparent 60%);
    transform: translate(-50%, -50%);
    pointer-events: none;
    transition: left 0.3s ease, top 0.3s ease;
    z-index: 0;
  }

  /* Hero reveal animations */
  .hero-eyebrow,
  .hero-accent-line,
  .report-h1,
  .hero-subtitle,
  .hero-body,
  .fl-kpi-grid {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .hero-eyebrow.hero-revealed {
    opacity: 1;
    transform: none;
    transition-delay: 0s;
  }

  .hero-accent-line.hero-revealed {
    opacity: 1;
    transform: none;
    transition-delay: 0.1s;
  }

  .report-h1.hero-revealed {
    opacity: 1;
    transform: none;
    transition-delay: 0.2s;
  }

  .hero-word-1 {
    display: inline-block;
  }

  .hero-word-2 {
    display: inline-block;
  }

  .hero-subtitle.hero-revealed {
    opacity: 1;
    transform: none;
    transition-delay: 0.35s;
  }

  .hero-body.hero-revealed {
    opacity: 1;
    transform: none;
    transition-delay: 0.45s;
  }

  .fl-kpi-grid.hero-revealed {
    opacity: 1;
    transform: none;
    transition-delay: 0.6s;
  }

  .hero-accent-line {
    width: 48px;
    height: 3px;
    background: linear-gradient(90deg, #5CC61E, rgba(92, 198, 30, 0.3));
    border-radius: 2px;
    margin: 20px 0;
    animation: accentPulse 3s ease-in-out infinite;
  }

  @keyframes accentPulse {
    0%, 100% {
      width: 48px;
      opacity: 1;
    }
    50% {
      width: 80px;
      opacity: 0.8;
    }
  }

  .text-fl-green {
    color: #5CC61E;
  }

  .hero-inner {
    position: relative;
    z-index: 1;
  }

  .hero-eyebrow {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: #5CC61E;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 20px;
  }

  .report-h1 {
    font-family: Georgia, serif;
    font-size: clamp(3.5rem, 7vw, 6rem);
    font-weight: 700;
    line-height: 1.0;
    color: #fff;
    margin: 0 0 16px;
  }

  .hero-subtitle {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 1.35rem;
    font-weight: 600;
    color: #5CC61E;
    margin-bottom: 20px;
    line-height: 1.4;
  }

  .hero-body {
    font-family: Georgia, serif;
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.72);
    max-width: 620px;
    line-height: 1.75;
    margin: 0;
  }

  /* ─── Animated category chart ────────────────────────────────────── */
  .category-chart-wrapper {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    padding: 40px 48px;
    box-shadow: 0 4px 24px rgba(33, 36, 74, 0.06);
  }

  .category-chart-title {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: #21244A;
    margin-bottom: 4px;
  }

  .category-chart-subtitle {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 12px;
    color: #9ca3af;
    margin-bottom: 28px;
    letter-spacing: 0.02em;
  }

  .category-bars {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .category-bar-row {
    display: grid;
    grid-template-columns: 140px 1fr;
    align-items: center;
    gap: 16px;
  }

  .category-bar-label {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    color: #6b7280;
    text-align: right;
    white-space: nowrap;
  }

  .category-bar-track {
    position: relative;
    height: 28px;
    background: #f3f4f6;
    border-radius: 6px;
    overflow: visible;
    display: flex;
    align-items: center;
  }

  .category-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #21244A 0%, #5CC61E 100%);
    border-radius: 6px;
    transition: width 1.2s cubic-bezier(0.16, 1, 0.3, 1);
    min-width: 0;
  }

  .category-bar-pct {
    position: absolute;
    right: -40px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #21244A;
  }

  /* ─── Friction pillars ───────────────────────────────────────────── */
  .fric-pillars {
    margin: 8px 0 32px;
    border-top: 2px solid #21244A;
  }

  .fric-pillar {
    display: flex;
    align-items: flex-start;
    gap: 0;
    border-bottom: 1px solid #D9E2DA;
    position: relative;
  }

  .fric-pillar--last {
    border-bottom: 2px solid #21244A;
  }

  .fric-pillar-left {
    width: 96px;
    flex-shrink: 0;
    padding: 28px 24px 28px 0;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    border-right: 1px solid #D9E2DA;
  }

  .fric-pillar-num {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: #5CC61E;
    line-height: 1;
  }

  .fric-pillar-right {
    flex: 1;
    padding: 28px 0 28px 32px;
  }

  .fric-pillar-eyebrow {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #5CC61E;
    margin-bottom: 6px;
  }

  .fric-pillar-title {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 20px;
    font-weight: 800;
    color: #21244A;
    margin-bottom: 14px;
    line-height: 1.2;
  }

  .fric-pillar-body {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 15px;
    line-height: 1.7;
    color: #4b5563;
    margin: 0;
  }

  .fric-pillar-body + .fric-pillar-body {
    margin-top: 10px;
  }

  /* Pillars slide in from left instead of up */
  .fric-pillar.anim-fade-up {
    transform: translateX(-24px);
  }

  .fric-pillar.anim-fade-up.is-visible {
    transform: translateX(0);
  }

  /* ─── Retailer role list ──────────────────────────────────────────── */
  .retailer-role-list {
    display: flex;
    flex-direction: column;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid #D9E2DA;
    box-shadow: 0 2px 12px rgba(33, 36, 74, 0.06);
    margin: 8px 0 32px;
  }

  .retailer-role-item {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    padding: 28px;
    background: #fff;
    border-bottom: 1px solid #f0f4f0;
  }

  .retailer-role-item:last-child {
    border-bottom: none;
  }

  .retailer-role-num {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 28px;
    font-weight: 900;
    color: rgba(92, 198, 30, 0.35);
    line-height: 1.1;
    flex-shrink: 0;
    min-width: 44px;
  }

  .retailer-role-title {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: #21244A;
    margin-bottom: 6px;
    line-height: 1.3;
  }

  .retailer-role-body {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 16px;
    line-height: 1.65;
    color: #4b5563;
    margin: 0;
  }

  /* ─── Pull quote ──────────────────────────────────────────────────── */
  .pull-quote {
    position: relative;
    margin: 32px 0;
    padding: 28px 32px 28px 48px;
    background: linear-gradient(135deg, rgba(92, 198, 30, 0.06) 0%, transparent 100%);
    border-left: 3px solid #5CC61E;
    border-radius: 0 12px 12px 0;
  }

  .pull-quote-mark {
    position: absolute;
    top: -8px;
    left: 18px;
    font-size: 80px;
    line-height: 1;
    color: #5CC61E;
    opacity: 0.2;
    font-family: Georgia, serif;
    pointer-events: none;
    user-select: none;
  }

  .pull-quote-text {
    font-family: Georgia, serif;
    font-size: 1.275rem;
    font-style: italic;
    line-height: 1.75;
    color: #21244A;
    margin: 0;
  }

  /* ─── Sticky story ───────────────────────────────────────────────── */
  .sticky-story {
    background: #21244A;
    position: relative;
    height: 400vh; /* 4 slides × 100vh — gives the pin room to scroll */
  }

  .sticky-story-pin {
    position: sticky;
    top: 0;
    height: var(--sc-h, 100vh);
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: var(--sc-h, 100vh);
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 48px;
  }

  .sticky-story-left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 80px 60px 80px 0;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
  }

  .sticky-stat-display {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .sticky-stat-eyebrow {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(92, 198, 30, 0.5);
    margin-bottom: 16px;
    transition: opacity 0.14s ease;
  }

  .sticky-stat-number {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: clamp(5rem, 10vw, 9rem);
    font-weight: 900;
    color: #5CC61E;
    line-height: 1;
    letter-spacing: -0.04em;
    transition: opacity 0.14s ease;
  }

  .sticky-stat-unit {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    transition: opacity 0.14s ease;
  }

  .sticky-story-dots {
    display: flex;
    gap: 8px;
    margin-top: 48px;
  }

  .sticky-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    transition: background 0.3s ease, width 0.3s ease;
  }

  .sticky-dot.active {
    background: #5CC61E;
    width: 24px;
    border-radius: 3px;
  }

  .sticky-story-right-viewport {
    overflow: hidden;
    height: var(--sc-h, 100vh);
    display: flex;
    align-items: stretch;
  }

  .sticky-story-track {
    width: 100%;
    will-change: transform;
    /* no CSS transition — driven by JS for 1:1 scroll feel */
  }

  .story-slide {
    height: var(--sc-h, 100vh);
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 80px 0 80px 60px;
  }

  .story-slide-eyebrow {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(92, 198, 30, 0.6);
    margin-bottom: 20px;
  }

  .story-slide-title {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: clamp(1.4rem, 2.5vw, 1.9rem);
    font-weight: 700;
    color: #fff;
    line-height: 1.3;
    margin: 0 0 20px;
  }

  .story-slide-body {
    font-family: Georgia, serif;
    font-size: 1.175rem;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.8;
    margin: 0;
  }

  /* ─── Display stat moment ────────────────────────────────────────── */
  .display-stat-moment {
    text-align: center;
    padding: 80px 0 60px;
  }

  .display-stat-number {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: clamp(6rem, 14vw, 12rem);
    font-weight: 900;
    color: #21244A;
    line-height: 1;
    letter-spacing: -0.05em;
  }

  .display-stat-suffix {
    color: #5CC61E;
  }

  .display-stat-label {
    font-family: Georgia, serif;
    font-size: 1.2rem;
    color: #21244A;
    max-width: 480px;
    margin: 16px auto 8px;
    line-height: 1.5;
  }

  .display-stat-sub {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 12px;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  /* KPI cards — dark variant inside hero */
  .fl-kpi {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-top: 3px solid #5CC61E;
    border-radius: 16px;
    padding: 28px 24px;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease, background 0.3s ease;
    cursor: default;
  }

  .fl-kpi:hover {
    transform: translateY(-6px);
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(92, 198, 30, 0.2);
  }

  .kpi-number {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 2.8rem;
    font-weight: 800;
    color: #fff;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .kpi-label {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 8px;
    letter-spacing: 0.02em;
  }

  /* ─── Section headers ────────────────────────────────────────────── */
  .section-header-block {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 40px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e5e7eb;
  }

  .section-number-badge {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 6rem;
    font-weight: 900;
    line-height: 1;
    color: #5CC61E;
    opacity: 0.12;
    flex-shrink: 0;
    width: 80px;
    text-align: right;
    margin-top: -10px;
    letter-spacing: -0.03em;
  }

  .section-header-content {
    flex: 1;
  }

  .section-title-text {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 0.7rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: #21244A;
    margin: 0 0 10px;
  }

  .section-title-bar {
    height: 3px;
    width: 48px;
    background: #5CC61E;
    border-radius: 2px;
  }

  /* ─── Body typography ────────────────────────────────────────────── */
  .text-body {
    font-family: Georgia, serif;
    font-size: 19px;
    line-height: 1.85;
  }

  .subsection-heading {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #21244A;
    padding-left: 14px;
    border-left: 3px solid #5CC61E;
  }

  /* ─── Callout / pull-quote ───────────────────────────────────────── */
  .border-l-fl {
    border-left: 4px solid #5CC61E;
    padding-left: 28px;
    background: #f8fdf5;
    border-radius: 0 10px 10px 0;
    padding-top: 16px;
    padding-bottom: 16px;
    padding-right: 24px;
  }

  /* ─── Full-width pull quote ──────────────────────────────────────── */
  .quote-block {
    position: relative;
    background: linear-gradient(135deg, #21244A 0%, #181b3e 100%);
    border-radius: 24px;
    padding: 72px 80px 64px;
    margin: 80px 0;
    font-family: Georgia, serif;
    font-size: 1.7rem;
    font-weight: 700;
    color: #fff;
    line-height: 1.55;
    overflow: hidden;
    box-shadow: 0 40px 80px rgba(33, 36, 74, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }

  .quote-block::before {
    content: '\201C';
    position: absolute;
    top: 8px;
    left: 52px;
    font-size: 11rem;
    line-height: 1;
    color: #5CC61E;
    opacity: 0.2;
    font-family: Georgia, serif;
    pointer-events: none;
  }

  .quote-block::after {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(92, 198, 30, 0.07) 0%, transparent 65%);
    pointer-events: none;
  }

  /* ─── Takeaway block ─────────────────────────────────────────────── */
  .fl-takeaway {
    background: #f5faf2;
    border-left: 5px solid #5CC61E;
    border-radius: 0 14px 14px 0;
    padding: 28px 32px;
  }

  /* ─── Signal Card (premium redesign) ────────────────────────────── */
  .fl-signal-card {
    border: 1px solid #D9E2DA;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(33, 36, 74, 0.09);
    background: #fff;
  }

  .fl-signal-card-header {
    background: #21244A;
    padding: 28px 36px;
    display: flex;
    align-items: center;
    gap: 28px;
  }

  .signal-num {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 60px;
    font-weight: 800;
    color: #5CC61E;
    line-height: 1;
    flex-shrink: 0;
    opacity: 0.85;
  }

  .signal-header-right {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .signal-eyebrow {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(92, 198, 30, 0.65);
  }

  .signal-title {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    margin: 0;
    line-height: 1.3;
  }

  .signal-rows {
    padding: 0;
  }

  .signal-row {
    padding: 22px 36px 22px 32px;
    border-bottom: 1px solid #f0f2f0;
    border-left: 4px solid transparent;
  }

  .signal-row:last-child {
    border-bottom: none;
  }

  .signal-row--observe {
    border-left-color: #9ca3af;
  }

  .signal-row--reveals {
    border-left-color: #21244A;
  }

  .signal-row--implication {
    border-left-color: #5CC61E;
    background: #f5faf2;
  }

  .signal-row-label {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 7px;
  }

  .signal-row--observe .signal-row-label {
    color: #9ca3af;
  }

  .signal-row--reveals .signal-row-label {
    color: #21244A;
  }

  .signal-row--implication .signal-row-label {
    color: #5CC61E;
  }

  .signal-row-text {
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 14px;
    line-height: 1.75;
    color: #374151;
    margin: 0;
  }

  .signal-row--implication .signal-row-text {
    color: #21244A;
    font-weight: 600;
  }

  .signal-appendix {
    padding: 11px 36px;
    background: #f8f9fb;
    border-top: 1px solid #D9E2DA;
    font-size: 11px;
    color: #9ca3af;
    font-style: italic;
  }

  .fl-infographic {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(33, 36, 74, 0.1);
    transition: box-shadow 0.3s ease, transform 0.3s ease;
  }

  .fl-infographic:hover {
    box-shadow: 0 20px 48px rgba(33, 36, 74, 0.16);
    transform: translateY(-2px);
  }

  .fl-infographic-header {
    background: #21244A;
    color: #fff;
    padding: 14px 22px;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .fl-infographic-subheader {
    background: #f5faf2;
    padding: 8px 18px;
    font-size: 12px;
    color: #6b7280;
    border-bottom: 1px solid #D9E2DA;
  }

  /* ── Appendix signal cards ───────────────────────────────────── */
  .ax-signals {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .ax-sig {
    position: relative;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(33, 36, 74, 0.08);
    border: 1px solid #D9E2DA;
    transition: box-shadow 0.3s ease, transform 0.3s ease;
  }

  .ax-sig:hover {
    box-shadow: 0 16px 48px rgba(33, 36, 74, 0.14);
    transform: translateY(-2px);
  }

  .ax-sig-watermark {
    position: absolute;
    top: -18px;
    right: 20px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 130px;
    font-weight: 900;
    line-height: 1;
    color: rgba(33, 36, 74, 0.04);
    pointer-events: none;
    user-select: none;
    letter-spacing: -0.05em;
  }

  .ax-sig-head {
    display: flex;
    align-items: center;
    gap: 20px;
    background: #21244A;
    padding: 20px 28px;
    position: relative;
    z-index: 1;
  }

  .ax-sig-num {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 36px;
    font-weight: 900;
    color: #5CC61E;
    line-height: 1;
    flex-shrink: 0;
    letter-spacing: -0.04em;
  }

  .ax-sig-eyebrow {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: rgba(92, 198, 30, 0.7);
    margin-bottom: 4px;
  }

  .ax-sig-title {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    line-height: 1.4;
  }

  .ax-sig-text {
    padding: 20px 28px 16px;
    font-size: 14px;
    color: #4b5563;
    line-height: 1.7;
    margin: 0;
    position: relative;
    z-index: 1;
  }

  .ax-sig-img {
    display: block;
    width: 100%;
    position: relative;
    z-index: 1;
  }

  .ax-sig-img--sep {
    border-top: 1px solid #e5e7eb;
  }

  /* Table cells */
  .infographic-th-green {
    background: #5CC61E;
    color: #fff;
    padding: 11px 18px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .infographic-th-navy {
    background: #21244A;
    color: #fff;
    padding: 11px 18px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .infographic-th-grey {
    background: #6b7280;
    color: #fff;
    padding: 11px 18px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .infographic-th-phase {
    background: #f5faf2;
    color: #21244A;
    padding: 11px 18px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #D9E2DA;
  }

  .infographic-td-phase {
    background: #f5faf2;
    color: #21244A;
    padding: 12px 18px;
    font-size: 13px;
    border-bottom: 1px solid #D9E2DA;
    border-right: 1px solid #D9E2DA;
    vertical-align: top;
  }

  .infographic-td-left {
    background: #fff;
    color: #374151;
    padding: 12px 18px;
    font-size: 13px;
    border-bottom: 1px solid #D9E2DA;
    border-right: 1px solid #D9E2DA;
    vertical-align: top;
  }

  .infographic-td-right {
    background: #fff;
    color: #374151;
    padding: 12px 18px;
    font-size: 13px;
    border-bottom: 1px solid #D9E2DA;
    vertical-align: top;
  }

  .infographic-row {
    background: #fff;
  }

  .infographic-row-alt {
    background: #fafafa;
  }

  .infographic-row td,
  .infographic-row-alt td {
    border-bottom: 1px solid #D9E2DA;
    vertical-align: top;
  }

  /* ─── Scroll fade-up animations ─────────────────────────────────── */
  .anim-fade-up {
    opacity: 0;
    transform: translateY(36px);
    transition: opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1), transform 0.85s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .anim-fade-up.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* ─── Signal card wrapper hover lift ────────────────────────────── */
  .signal-card-wrapper {
    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s ease;
    border-radius: 14px;
  }

  .signal-card-wrapper:hover {
    transform: translateY(-5px);
    box-shadow: 0 28px 56px rgba(33, 36, 74, 0.16);
  }

  /* ─── Responsive ─────────────────────────────────────────────────── */
  @media (max-width: 768px) {
    .fl-container {
      padding: 0 20px;
    }

    .report-h1 {
      font-size: 2.8rem;
    }

    .hero-section {
      padding: 64px 0 56px;
    }

    .quote-block {
      font-size: 1.25rem;
      padding: 48px 32px 40px;
    }

    .quote-block::before {
      font-size: 6rem;
      top: 8px;
      left: 24px;
    }

    .section-number-badge {
      font-size: 3rem;
      width: 44px;
    }

    .fl-infographic-header {
      font-size: 12px;
      padding: 11px 16px;
    }

    .sticky-story-pin {
      grid-template-columns: 1fr;
      padding: 0 20px;
    }

    .sticky-story-left {
      display: none;
    }

    .story-slide {
      padding: 60px 0;
      min-height: auto;
    }

    .sticky-story {
      height: auto;
    }

    .sticky-story-right-viewport {
      height: auto;
      overflow: visible;
    }

    .sticky-story-track {
      transform: none !important;
    }

    .category-chart-wrapper {
      padding: 24px 20px;
    }

    .category-bar-row {
      grid-template-columns: 100px 1fr;
    }

    .display-stat-number {
      font-size: clamp(4rem, 20vw, 7rem);
    }

    .fric-pillar-left {
      width: 56px;
      padding: 20px 16px 20px 0;
    }

    .fric-pillar-right {
      padding: 20px 0 20px 20px;
    }

    .fric-pillar-title {
      font-size: 17px;
    }

    .retailer-role-item {
      gap: 16px;
      padding: 20px;
    }

    .retailer-role-num {
      font-size: 22px;
      min-width: 32px;
    }

    .pull-quote {
      padding: 20px 20px 20px 36px;
    }

    .pull-quote-text {
      font-size: 1rem;
    }
  }

  /* ─── Print ──────────────────────────────────────────────────────── */
  @media print {
    .no-print {
      display: none !important;
    }

    .hero-section {
      background: #21244A !important;
      -webkit-print-color-adjust: exact;
    }
  }

  /* ─── Side section progress nav ─────────────────────────────────── */
  .side-nav {
    position: fixed;
    right: 36px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 40;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0;
  }

  .side-nav-line {
    position: absolute;
    top: 14px;
    bottom: 14px;
    right: 5px;
    width: 1px;
    background: linear-gradient(to bottom, transparent, rgba(33, 36, 74, 0.12) 20%, rgba(33, 36, 74, 0.12) 80%, transparent);
    pointer-events: none;
  }

  .side-nav-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 9px 0;
    text-decoration: none;
    cursor: pointer;
  }

  .side-nav-dot {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: rgba(33, 36, 74, 0.18);
    border: 2px solid transparent;
    flex-shrink: 0;
    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
    z-index: 1;
  }

  .side-nav-item.is-active .side-nav-dot {
    background: #5CC61E;
    border-color: rgba(92, 198, 30, 0.35);
    box-shadow: 0 0 0 5px rgba(92, 198, 30, 0.15);
    transform: scale(1.5);
  }

  .side-nav-item:hover .side-nav-dot {
    background: rgba(33, 36, 74, 0.4);
    transform: scale(1.2);
  }

  .side-nav-label {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: rgba(33, 36, 74, 0.35);
    white-space: nowrap;
    transition: color 0.25s ease, font-weight 0.25s ease;
    pointer-events: none;
  }

  .side-nav-item.is-active .side-nav-label {
    color: #21244A;
    font-weight: 700;
    font-size: 13px;
  }

  .side-nav-lock {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 4px;
    vertical-align: -1px;
    opacity: 0.6;
  }

  .side-nav-item.is-locked {
    pointer-events: none;
    cursor: default;
  }

  .side-nav-item.is-locked .side-nav-label {
    color: rgba(33, 36, 74, 0.25);
  }

  .side-nav-item.is-locked .side-nav-dot {
    background: rgba(33, 36, 74, 0.1);
  }

  .side-nav-item:hover .side-nav-label {
    color: rgba(33, 36, 74, 0.65);
  }

  /* ── Dark variant: while a dark block is behind the nav, flip to white text with a dark glow ── */
  .side-nav--on-dark .side-nav-label {
    color: rgba(255, 255, 255, 0.65);
    text-shadow:
      0 0 4px rgba(24, 27, 62, 1),
      0 0 8px rgba(24, 27, 62, 0.95),
      0 0 16px rgba(24, 27, 62, 0.85),
      0 0 28px rgba(24, 27, 62, 0.7);
  }

  .side-nav--on-dark .side-nav-item.is-active .side-nav-label {
    color: #fff;
  }

  .side-nav--on-dark .side-nav-item:hover .side-nav-label {
    color: rgba(255, 255, 255, 0.9);
  }

  .side-nav--on-dark .side-nav-item.is-locked .side-nav-label {
    color: rgba(255, 255, 255, 0.35);
  }

  .side-nav--on-dark .side-nav-dot {
    background: rgba(255, 255, 255, 0.35);
  }

  .side-nav--on-dark .side-nav-item:hover .side-nav-dot {
    background: rgba(255, 255, 255, 0.6);
  }

  .side-nav--on-dark .side-nav-item.is-locked .side-nav-dot {
    background: rgba(255, 255, 255, 0.2);
  }

  .side-nav--on-dark .side-nav-line {
    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.25) 20%, rgba(255, 255, 255, 0.25) 80%, transparent);
  }

  @media (max-width: 1280px) {
    .side-nav {
      display: none;
    }
  }

  /* ─── Horizontal signal scroll ────────────────────────────────────── */
  .signals-hscroll {
    position: relative;
    background: #166534;
  }

  .signals-hscroll-pin {
    position: sticky;
    top: 0;
    height: 100vh;
    overflow: hidden;
    background: inherit;
  }

  .sh-pin-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .signals-hscroll-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 48px 20px;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    gap: 24px;
  }

  .sh-eyebrow {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(92, 198, 30, 0.7);
    margin-bottom: 3px;
  }

  .sh-bar-title {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 17px;
    font-weight: 700;
    color: #fff;
  }

  .sh-dots {
    display: flex;
    gap: 8px;
    align-items: center;
    flex: 1;
    justify-content: center;
  }

  .sh-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  .sh-dot.is-active {
    background: #5CC61E;
    transform: scale(1.5);
    box-shadow: 0 0 0 3px rgba(92, 198, 30, 0.2);
  }

  .sh-counter {
    font-family: 'Inter', system-ui, sans-serif;
    display: flex;
    align-items: baseline;
    gap: 2px;
    flex-shrink: 0;
  }

  .sh-counter-current {
    font-size: 30px;
    font-weight: 900;
    color: #5CC61E;
    line-height: 1;
    min-width: 32px;
    text-align: right;
    display: inline-block;
  }

  .sh-counter-denom {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.28);
  }

  .sh-viewport {
    flex: 1;
    overflow: hidden;
    padding: 16px 0 56px;
    display: flex;
    align-items: center;
  }

  /* ── Scroll hint (fixed to viewport, shown while in signals section) ── */
  .sh-scroll-hint {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 50;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    padding: 10px 20px 14px;
    background: rgba(22, 101, 52, 0.85);
    backdrop-filter: blur(8px);
    border-radius: 12px;
    border: 1px solid rgba(92, 198, 30, 0.25);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  .sh-scroll-hint-text {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;
  }

  .sh-scroll-hint-arrow {
    width: 22px;
    height: 22px;
    color: #5CC61E;
    animation: sh-hint-bounce 1.6s ease-in-out infinite;
  }

  @keyframes sh-hint-bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(7px);
    }
  }

  .sh-track {
    display: flex;
    gap: 20px;
    align-items: stretch;
    will-change: transform;
    /* center first card in the visible area */
    padding-left: calc(50% - 22vw);
    padding-right: calc(50% - 22vw);
  }

  @media (max-width: 1400px) {
    .sh-track {
      padding-left: calc(50% - 30vw);
      padding-right: calc(50% - 30vw);
    }
  }

  @media (max-width: 900px) {
    .sh-track {
      padding-left: calc(50% - 40vw);
      padding-right: calc(50% - 40vw);
    }
  }

  /* ── Signal card — frosted dark ───────────────────────────────── */
  .sh-card {
    width: 44vw;
    flex-shrink: 0;
    max-height: calc(100vh - 280px);
    background: rgba(0, 0, 0, 0.38);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: border-color 0.3s ease, transform 0.3s ease;
  }

  @media (max-width: 1400px) {
    .sh-card {
      width: 60vw;
      max-height: calc(100vh - 300px);
    }

    .sh-card-inner {
      padding: 20px 24px 16px;
      gap: 16px;
    }

    .sh-card-title {
      font-size: clamp(13px, 1.3vw, 17px);
    }

    .sh-entry {
      padding: 10px 0;
    }

    .sh-entry-text {
      font-size: 13.5px;
      line-height: 1.62;
    }

    .sh-entry--impl .sh-entry-text {
      font-size: 13px;
    }
  }

  @media (max-width: 900px) {
    .sh-card {
      width: 80vw;
    }
  }

  .sh-card:hover {
    border-color: rgba(92, 198, 30, 0.4);
    transform: translateY(-3px);
  }

  .sh-card-watermark {
    position: absolute;
    top: -12px;
    right: 24px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 120px;
    font-weight: 900;
    color: rgba(255, 255, 255, 0.04);
    line-height: 1;
    pointer-events: none;
    user-select: none;
    letter-spacing: -0.05em;
  }

  .sh-card-inner {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    padding: 32px 36px 28px;
    gap: 28px;
  }

  @media (max-width: 768px) {
    .sh-card-inner {
      padding: 24px 24px 20px;
      gap: 20px;
    }
  }

  .sh-card-top {
    flex-shrink: 0;
  }

  .sh-card-label {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: #5CC61E;
    display: block;
    margin-bottom: 10px;
  }

  .sh-card-title {
    font-family: Georgia, serif;
    font-size: clamp(16px, 1.7vw, 22px);
    font-weight: 700;
    color: #fff;
    line-height: 1.35;
    margin: 0;
  }

  .sh-card-body {
    flex: 1;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .sh-entry {
    padding: 16px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.07);
  }

  .sh-entry:first-child {
    border-top: none;
    padding-top: 0;
  }

  .sh-entry--impl {
    padding-bottom: 0;
  }

  .sh-entry-tag {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(255, 255, 255, 0.35);
    margin-bottom: 7px;
  }

  .sh-entry--impl .sh-entry-tag {
    color: #5CC61E;
  }

  .sh-entry-text {
    font-family: Georgia, serif;
    font-size: 15.5px;
    line-height: 1.72;
    color: rgba(255, 255, 255, 0.78);
    margin: 0;
  }

  .sh-entry--impl .sh-entry-text {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.92);
  }

  /* ─── Section 4: Value chain scroll ─────────────────────────────── */
  .vc-section {
    position: relative;
  }

  .vc-pin {
    position: sticky;
    top: 0;
    height: var(--sc-h, 100vh);
    background: #0d1117;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* Chain navigation bar */
  .vc-chain-bar {
    height: 76px;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    display: flex;
    align-items: center;
    padding: 0 48px;
  }

  .vc-chain-inner {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .vc-node {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    flex-shrink: 0;
  }

  .vc-node-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    transition: background 0.35s ease, transform 0.35s ease, box-shadow 0.35s ease;
  }

  .vc-node-label {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.18);
    white-space: nowrap;
    transition: color 0.35s ease;
  }

  .vc-connector {
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.07);
    margin-bottom: 16px;
    transition: background 0.35s ease;
  }

  /* Body */
  .vc-body {
    flex: 1;
    display: flex;
    overflow: hidden;
    min-height: 0;
  }

  /* Left: actor identity */
  .vc-actor-panel {
    width: 300px;
    flex-shrink: 0;
    padding: 48px 36px 48px 48px;
    border-right: 1px solid rgba(255, 255, 255, 0.06);
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 14px;
  }

  .vc-actor-num {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 5.5rem;
    font-weight: 900;
    line-height: 1;
    letter-spacing: -0.05em;
    color: #22c55e;
    transition: opacity 0.14s ease, color 0.14s ease;
  }

  .vc-actor-name {
    font-family: Georgia, serif;
    font-size: 1.5rem;
    color: #fff;
    line-height: 1.45;
    transition: opacity 0.14s ease;
  }

  .vc-actor-of {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.22);
  }

  /* Right: sliding content */
  .vc-right-vp {
    flex: 1;
    overflow: hidden;
    min-width: 0;
  }

  .vc-track {
    display: flex;
    flex-direction: column;
    will-change: transform;
  }

  .vc-slide {
    height: calc(var(--sc-h, 100vh) - 76px);
    flex-shrink: 0;
    padding: 36px 48px;
    display: flex;
    align-items: center;
  }

  .vc-rows-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    width: 100%;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 12px;
    overflow: hidden;
  }

  .vc-row {
    background: #0d1117;
    padding: 28px 30px;
  }

  .vc-row-tag {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    margin-bottom: 10px;
  }

  .vc-row-text {
    font-family: Georgia, serif;
    font-size: 15.5px;
    line-height: 1.75;
    color: rgba(255, 255, 255, 0.68);
    margin: 0;
  }

  /* Progress dots */
  .vc-dots-row {
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }

  .vc-dot-item {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    transition: all 0.3s ease;
  }

  .vc-dot-item.is-active {
    background: #5CC61E;
    transform: scale(1.5);
    box-shadow: 0 0 0 3px rgba(92, 198, 30, 0.2);
  }

  /* ─── Key Findings panels ────────────────────────────────────────── */
  .story-panels {
    background: #21244A;
  }

  .story-panel {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  .story-panel-inner {
    max-width: 1100px;
    margin: 0 auto;
    padding: 80px 48px;
    display: grid;
    grid-template-columns: 260px 1px 1fr;
    gap: 0 56px;
    align-items: center;
    min-height: 40vh;
  }

  .story-panel-stat {
    position: relative;
  }

  .story-panel-number {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: clamp(4rem, 7vw, 6.5rem);
    font-weight: 900;
    color: #5CC61E;
    line-height: 1;
    letter-spacing: -0.04em;
  }

  .story-panel-unit {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.35);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-top: 10px;
  }

  .story-panel-index {
    position: absolute;
    top: -16px;
    left: 0;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 11px;
    font-weight: 700;
    color: rgba(92, 198, 30, 0.4);
    letter-spacing: 0.12em;
  }

  .story-panel-divider {
    width: 1px;
    height: 120px;
    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.12), transparent);
    align-self: center;
  }

  .story-panel-content {
    padding-left: 0;
  }

  @media (max-width: 768px) {
    .story-panel-inner {
      grid-template-columns: 1fr;
      gap: 24px;
      padding: 48px 24px;
    }

    .story-panel-divider {
      display: none;
    }
  }
</style>
